/*
 * Oak & Ember — interactive 3D showroom (<oak-room-3d>)
 * ------------------------------------------------------
 * Preview mode  : gentle parallax on arrow keys / scroll / pointer, every furniture piece is clickable.
 * Walk mode     : first-person walk-through (arrow keys / WASD + drag to look, on-screen D-pad on touch).
 * Phones        : the field of view and the preview camera follow the SHAPE of the stage (an upright phone sees the whole
 *                 room, not a keyhole); on-screen arrows on any touch or narrow screen; a Walk button inside the stage.
 * Performance   : textures stay sharp, everything else gives way first. A quality ladder (see initQuality) sheds the
 *                 environment-lit shading, then resolution above 1.5x, then shadows, and never goes below one canvas pixel
 *                 per CSS pixel; weak phone GPUs start on the cheap path. Frames are drawn only when something changes
 *                 (30fps for the idle sway, nothing while a phone scrolls, the loop sleeps when a phone is idle),
 *                 materials are shared, matrices frozen, the shadow map is static, raycasts use a BVH.
 *                 Add ?oak3d=debug to the page address for a live readout of what the room decided on a device.
 * Depends on window.OAK_THREE, provided by oak-vendor.js (loaded lazily by this element).
 */
(function () {
  'use strict';
  if (customElements.get('oak-room-3d')) return;

  /* ---------- furniture naming helpers ---------- */
  // three.js strips dots from node names ("chair.002" -> "chair002"), so names are normalised the same way here.
  var EXCLUDE_BASE = /^(walls|molding|outlet|outlet_small|vent|light_switch|door_frame|door|window|)$/i;
  var EXCLUDE_NAME = /^(Cube059|Cylinder010)$/i;
  function normName(n) { return String(n || '').replace(/\./g, '').trim(); }
  function isExcluded(name) { return EXCLUDE_NAME.test(name) || EXCLUDE_BASE.test(baseName(name)); }
  var LABELS = [
    [/^coach$/i, 'Sofa'], [/^chair_2$/i, 'Dining chair'], [/^chair$/i, 'Bar stool'],
    [/^table_small$/i, 'Side table'], [/^table$/i, 'Dining table'], [/^coffee_table$/i, 'Coffee table'],
    [/^side_table$/i, 'Nightstand'], [/^tv_table$/i, 'TV console'], [/^tv$/i, 'Television'],
    [/^kitchen_island$/i, 'Kitchen island'], [/^kitchen_cabinets$/i, 'Kitchen cabinets'],
    [/^fridge$/i, 'Refrigerator'], [/^stove$/i, 'Range cooker'], [/^hood$/i, 'Range hood'],
    [/^microwave$/i, 'Microwave'], [/^sink$/i, 'Sink'], [/^faucet$/i, 'Faucet'],
    [/^carpet$/i, 'Rug'], [/^pillow$/i, 'Cushion'], [/^ottoman$/i, 'Ottoman'],
    [/^hallway_cabinet$/i, 'Hallway cabinet'], [/^closet_door$/i, 'Closet'], [/^mirror$/i, 'Mirror'],
    [/^painting(_\d)?$/i, 'Wall art'], [/^curtain$/i, 'Curtains'], [/^blinds$/i, 'Blinds'],
    [/^rail$/i, 'Curtain rail'], [/^light$/i, 'Pendant light'], [/^counter_lights$/i, 'Counter lights'],
    [/^Cylinder$/i, 'Pendant lamps'], [/^book$/i, 'Books'], [/^bowl$/i, 'Bowl'], [/^plate$/i, 'Plate'],
    [/^container$/i, 'Canister'], [/^(plant_fern|strelitzia|succulent|broadleaf|plant_pot|pot)$/i, 'Plant'],
    [/^Cube$/i, 'Bed'], [/^Torus$/i, 'Floor cushion'], [/^bed$/i, 'Bed']
  ];
  function baseName(n) { return normName(n).replace(/\.?\d{3}$/, '').trim(); }
  function prettyLabel(name) {
    var b = baseName(name);
    for (var i = 0; i < LABELS.length; i++) if (LABELS[i][0].test(b)) return LABELS[i][1];
    b = b.replace(/[_-]+/g, ' ').trim();
    return b ? b.charAt(0).toUpperCase() + b.slice(1) : 'Furniture';
  }
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  var FOV_MAX = 90;          // vertical field of view limit on a very tall stage (beyond this the room looks stretched)
  var HEMI_FULL = 0.55;      // sky/ground light while the room is lit by its environment map
  var HEMI_LITE = 0.8;     // "lite" shading keeps the environment's soft light and only misses its faint mirror-like part
  var HEMI_NOENV = 3.75;     // weak GPUs have no environment map: sky light ...
  var FILL_NOENV = 3.3;      // ... plus a shadowless fill light stand in for it (values checked by measured brightness)
  function parseVec(str, fallback) {
    if (!str) return fallback.slice();
    var p = String(str).split(',').map(parseFloat);
    return p.length === 3 && p.every(function (n) { return !isNaN(n); }) ? p : fallback.slice();
  }

  var vendorPromise = null;
  function loadVendor(src) {
    if (window.OAK_THREE) return Promise.resolve(window.OAK_THREE);
    if (vendorPromise) return vendorPromise;
    vendorPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { window.OAK_THREE ? resolve(window.OAK_THREE) : reject(new Error('vendor missing')); };
      s.onerror = function () { reject(new Error('vendor failed')); };
      document.head.appendChild(s);
    });
    return vendorPromise;
  }

  class OakRoom extends HTMLElement {}

  OakRoom.prototype.connectedCallback = function () {
    if (this._booted) return;
    this._booted = true;
    var el = this;
    // "isTouch" describes the PRIMARY input (layout: D-pad, hints). Touchscreen laptops report touch points but are
    // driven by a mouse, so hover is decided per pointer event (lastPointerType) instead.
    // Several Android browsers claim "hover: hover" on a phone, so only the pointer's precision is trusted here;
    // and whatever the browser claims, the first real touch on the room switches the touch controls on (setTouchUi).
    var mm = function (q) { try { return window.matchMedia(q).matches; } catch (e) { return false; } };
    this.isTouch = mm('(pointer: coarse)') || ((navigator.maxTouchPoints || 0) > 0 && !mm('(pointer: fine)'));
    this.lastPointerType = this.isTouch ? 'touch' : 'mouse';
    this.classList.toggle('is-touch', this.isTouch);
    this.q = function (sel) { return el.querySelector(sel); };
    this.stage = this.q('[data-oak-stage]');
    this.canvas = this.q('canvas');
    this.ui = {
      loader: this.q('[data-oak-loader]'), bar: this.q('[data-oak-bar]'), poster: this.q('[data-oak-poster]'),
      walkBtns: this.querySelectorAll('[data-oak-walk]'), exitBtn: this.q('[data-oak-exit]'), hint: this.q('[data-oak-hint]'),
      dpad: this.q('[data-oak-dpad]'), card: this.q('[data-oak-card]'), markers: this.q('[data-oak-markers]'),
      price: this.q('[data-oak-price3d]'), viewBtn: this.q('[data-oak-view]'),
      fallback: this.q('[data-oak-fallback]')
    };
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mode = 'preview';
    this.keys = {};
    this.hotspots = [];
    try { var j = this.q('script[type="application/json"]'); if (j) this.hotspots = JSON.parse(j.textContent) || []; } catch (e) { this.hotspots = []; }
    this.previewPos = parseVec(this.dataset.previewPosition, [0.4, 2.1, 5.0]);
    this.previewTarget = parseVec(this.dataset.previewTarget, [-2.8, 0.75, 0.4]);
    // a tall, narrow stage (phones held upright) gets its own camera: closer to the sofa, the island out of the way
    this.previewPosTall = parseVec(this.dataset.previewPositionMobile, [-0.7, 1.8, 4.0]);
    this.previewTargetTall = parseVec(this.dataset.previewTargetMobile, [-3.3, 0.6, -0.3]);
    this.walkStart = parseVec(this.dataset.walkStart, [-1.2, 1.6, 4.4]);
    this.walkLook = parseVec(this.dataset.walkLook, [-4.0, 1.25, 0.8]);
    this.exposure = parseFloat(this.dataset.exposure || '1');
    this.setHint('preview');
    this.bindUi();
    this.ro = new ResizeObserver(function () { el.resize(); });
    this.ro.observe(this.stage);
    this.resize();
    if (!this.webglSupported()) { this.showFallback(); return; }
    var io = this.bootIo = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { io.disconnect(); el.bootSoon(); }
    }, { rootMargin: '400px' });
    io.observe(this);
  };
  /* The room sits at the top of the homepage: it starts once the page itself has finished loading and the browser has a
     quiet moment, so on a phone it never competes with the first paint, the fonts and the header. */
  OakRoom.prototype.bootSoon = function () {
    var el = this, fired = false;
    var go = function () {
      if (fired) return;
      fired = true;
      var idle = window.requestIdleCallback ? function (f) { window.requestIdleCallback(f, { timeout: 1200 }); } : function (f) { setTimeout(f, 60); };
      idle(function () { el.boot(); });
    };
    if (document.readyState === 'complete') go();
    else { window.addEventListener('load', go, { once: true }); setTimeout(go, 3000); }
  };

  // deferred: a DOM move (section reorder in the editor) reconnects immediately and must not tear the room down
  OakRoom.prototype.disconnectedCallback = function () {
    var el = this;
    setTimeout(function () { if (!el.isConnected) el.dispose(); }, 0);
  };

  OakRoom.prototype.webglSupported = function () {
    // three.js needs WebGL 2. The probe context is released straight away: browsers only allow a handful of live
    // contexts (8 on Android), and the theme editor creates this element again on every settings change.
    try {
      var gl = document.createElement('canvas').getContext('webgl2');
      if (!gl) return false;
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      return true;
    } catch (e) { return false; }
  };
  OakRoom.prototype.setWalkButtons = function (disabled) {
    Array.prototype.forEach.call(this.ui.walkBtns || [], function (b) { b.disabled = disabled; });
  };
  OakRoom.prototype.showFallback = function () {
    this.classList.add('is-fallback');
    if (this.ui.loader) this.ui.loader.hidden = true;
    if (this.ui.fallback) this.ui.fallback.hidden = false;
    this.setWalkButtons(true);
  };

  /* ---------- UI wiring ---------- */
  OakRoom.prototype.setHint = function (kind) {
    if (!this.ui.hint) return;
    this.hintKind = kind;
    // a narrow stage has the on-screen arrows even with a mouse (theme editor phone preview), so it gets the short wording too
    var touchy = this.isTouch || this.compact;
    var t = touchy
      ? (kind === 'walk' ? this.dataset.hintWalkTouch || 'Arrows to walk · drag to look · tap furniture' : this.dataset.hintPreviewTouch || 'Drag to look · tap a piece for its price')
      : (kind === 'walk' ? this.dataset.hintWalk || 'Arrow keys / WASD to move · drag to look · click furniture · Esc to exit' : this.dataset.hintPreview || 'Use ← ↑ → ↓ arrow keys to look around · hover a piece for its price · click for its category');
    // no linked product is visible (e.g. products still in Draft): do not promise prices or categories
    if (kind !== 'walk' && !this.hotspots.length) {
      t = touchy ? (this.dataset.hintPreviewTouchEmpty || 'Drag to look around') : (this.dataset.hintPreviewEmpty || 'Use the arrow keys or drag to look around');
    }
    this.ui.hint.textContent = t;
    // on a small screen the pill covers part of the room: it bows out after a few seconds (and returns on a mode change)
    var hint = this.ui.hint;
    hint.classList.remove('is-dismissed');
    clearTimeout(this.hintTimer);
    if (touchy) this.hintTimer = setTimeout(function () { hint.classList.add('is-dismissed'); }, kind === 'walk' ? 6000 : 9000);
    // the little prompt inside the card says "tap" on touch devices
    var tap = this.querySelector('[data-oak-card-tap]');
    if (tap) {
      if (!tap.hasAttribute('data-mouse-text')) tap.setAttribute('data-mouse-text', tap.textContent);
      tap.textContent = (this.isTouch && tap.getAttribute('data-touch-text')) || tap.getAttribute('data-mouse-text');
    }
  };
  /* The browser's media queries are only a first guess: the first real finger on the room switches the touch controls on. */
  OakRoom.prototype.setTouchUi = function (on) {
    if (this.isTouch === on) return;
    this.isTouch = on;
    this.classList.toggle('is-touch', on);
    this.setHint(this.hintKind || 'preview');
  };

  OakRoom.prototype.bindUi = function () {
    var el = this;
    Array.prototype.forEach.call(this.ui.walkBtns, function (b) { b.addEventListener('click', function () { el.enterWalk(); }); });
    if (this.ui.exitBtn) this.ui.exitBtn.addEventListener('click', function () { el.exitWalk(); });
    if (this.ui.viewBtn) this.ui.viewBtn.addEventListener('click', function () { el.toggleView(); });
    if (this.ui.card) {
      var close = this.ui.card.querySelector('[data-oak-card-close]');
      if (close) close.addEventListener('click', function (e) { e.stopPropagation(); el.select(null); });
      // a pinned "peek" card (touch: first tap) flips to the category when the card itself is tapped
      this.ui.card.addEventListener('click', function (e) {
        // A link tapped while walking: walk mode added a history entry for the Back button. Take it back first and
        // navigate afterwards, otherwise one Back press on the homepage would later do nothing.
        var a = e.target.closest('a[href]');
        if (a && el.mode === 'walk' && el.pushedState && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey && a.target !== '_blank') {
          e.preventDefault();
          el.pendingNav = a.href;
          el.exitWalk();   // -> history.back() -> popstate -> onPopState navigates
          setTimeout(function () { if (el.pendingNav) { var u = el.pendingNav; el.pendingNav = null; window.location.href = u; } }, 500);
          return;
        }
        if (e.target.closest('a,button')) return;
        if (el.pinned && el.cardState === 'peek' && el.cardItem) el.activate(el.cardItem);
      });
    }
    this.onBlur = function () { el.keys = {}; };
    window.addEventListener('blur', this.onBlur);
    if (this.ui.dpad) {
      // The four arrows are ONE touch surface: the thumb can slide from "forward" onto "left" without lifting (per-button
      // listeners kept walking straight, which felt like lag), and two thumbs can walk and turn at the same time.
      var pad = this.ui.dpad, btns = Array.prototype.slice.call(pad.querySelectorAll('[data-key]')), rects = [], held = {};
      var keyAt = function (x, y) {
        for (var i = 0; i < rects.length; i++) { var r = rects[i].r; if (x >= r.left - 6 && x <= r.right + 6 && y >= r.top - 6 && y <= r.bottom + 6) return rects[i].k; }
        return null;
      };
      var sync = function () {
        var on = {};
        Object.keys(held).forEach(function (id) { if (held[id]) on[held[id]] = true; });
        btns.forEach(function (b) { var k = b.getAttribute('data-key'); el.keys[k] = !!on[k]; b.classList.toggle('is-active', !!on[k]); });
        el.requestFrame();
      };
      var release = function (e) { if (e.pointerId in held) { delete held[e.pointerId]; sync(); } };
      pad.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        rects = btns.map(function (b) { return { k: b.getAttribute('data-key'), r: b.getBoundingClientRect() }; });   // once per press, not per move
        try { pad.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        held[e.pointerId] = keyAt(e.clientX, e.clientY);
        sync();
      });
      pad.addEventListener('pointermove', function (e) {
        if (!(e.pointerId in held)) return;
        var k = keyAt(e.clientX, e.clientY);
        if (k !== held[e.pointerId]) { held[e.pointerId] = k; sync(); }
      });
      pad.addEventListener('pointerup', release);
      pad.addEventListener('pointercancel', release);
      pad.addEventListener('lostpointercapture', release);
      pad.addEventListener('contextmenu', function (e) { e.preventDefault(); });
      this.releasePad = function () { held = {}; sync(); };
    }
    // leaving the page in walk mode (a category link, the Back button): never come back to a locked, fullscreen page
    this.onPageHide = function () { if (el.mode === 'walk') el.exitWalk(true); el.keys = {}; };
    this.onPageShow = function (e) { if (e.persisted) { el.pageHidden = document.hidden; el.requestFrame(); } };
    // the phone's Back button closes walk mode instead of leaving the shop
    this.onPopState = function () {
      if (el.pendingNav) { var u = el.pendingNav; el.pendingNav = null; window.location.href = u; return; }
      if (el.mode === 'walk') { el.pushedState = false; el.exitWalk(); }
    };
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('popstate', this.onPopState);
    this.onKeyDown = function (e) { el.handleKey(e, true); };
    this.onKeyUp = function (e) { el.handleKey(e, false); };
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.hovering = false;
    this.stage.addEventListener('pointerdown', function (e) { if (e.pointerType === 'touch') el.setTouchUi(true); }, true);
    // fullscreen walk mode: older iOS Safari ignores touch-action, so the page behind must not rubber-band or zoom.
    // The listener only exists while walking: a non-passive touch listener would slow down page scrolling over the preview.
    this.onWalkTouchMove = function (e) { if (e.cancelable) e.preventDefault(); };
    this.stage.addEventListener('pointerenter', function () { el.hovering = true; });
    this.stage.addEventListener('pointerleave', function () { el.hovering = false; el.pointerInside = false; });
    // the scroll parallax is drawn every frame on a desktop; on phones (and in lite quality) a smooth page scroll matters
    // more, so there it is drawn at the idle rate of 30fps
    this.onScroll = function () {
      el.lastScrollAt = performance.now();
      // Phones: the room holds still while the page scrolls. A 270-draw WebGL frame competing with the browser's own
      // scrolling is what makes a cheap phone feel laggy; the parallax is a desktop nicety.
      if (el.phoneClass) return;
      el.scrollDirty = true;
      el.lastInputAt = el.lastScrollAt;
      if (!(el.quality && el.quality.lite)) el.dirty = true;
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onVisibility = function () { el.pageHidden = document.hidden; el.requestFrame(); };
    document.addEventListener('visibilitychange', this.onVisibility);
  };

  var KEYMAP = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', w: 'w', a: 'a', s: 's', d: 'd', W: 'w', A: 'a', S: 's', D: 'd', Shift: 'shift' };
  OakRoom.prototype.handleKey = function (e, isDown) {
    if (e.key === 'Escape' && isDown) {
      if (this.pinned) { this.select(null); return; }   // Esc first closes an open card
      if (this.mode === 'walk') { this.exitWalk(); return; }
    }
    if (isDown && !e.repeat && (e.key === 'v' || e.key === 'V' || e.key === 'c' || e.key === 'C') && this.mode === 'walk' && !this.transition && !/INPUT|TEXTAREA|SELECT/.test((e.target && e.target.tagName) || '')) { this.toggleView(); return; }
    var k = KEYMAP[e.key];
    if (!k) return;
    if (!isDown) { this.keys[k] = false; return; }       // releases always register, so keys never stick
    if (!this.ready) return;                             // do not swallow arrow keys while loading / in fallback
    var tag = (e.target && e.target.tagName) || '';
    if (/INPUT|TEXTAREA|SELECT/.test(tag) || (e.target && e.target.isContentEditable)) return;
    var active = this.mode === 'walk' || this.hovering || this.contains(document.activeElement);
    if (!active) return;
    this.keys[k] = isDown;
    if (/^Arrow/.test(e.key)) e.preventDefault();
    this.requestFrame();
  };

  /* ---------- boot / scene ---------- */
  OakRoom.prototype.boot = function () {
    var el = this;
    if (this.booting || this.disposed) return;
    this.booting = true;
    // the 2MB model starts downloading together with the 3D engine instead of after it (about a second saved on 4G)
    this.modelBuf = this.fetchModel();
    loadVendor(this.dataset.vendor).then(function (T) {
      if (el.disposed || !el.isConnected) return;   // the theme editor removed this copy of the section while the script loaded
      el.T = T; el.initScene();
    }).catch(function () { if (!el.disposed) el.showFallback(); });
  };

  OakRoom.prototype.initScene = function () {
    var T = this.T, el = this;
    var renderer;
    // GPU settings follow the DEVICE (a small screen), never the kind of input: a phone that reports a mouse or a pen
    // is still a phone, and the theme editor's phone preview still runs on a desktop GPU.
    var shortSide = Math.min(window.screen && screen.width || 9999, window.screen && screen.height || 9999);
    this.phoneClass = this.isTouch || shortSide < 600;
    var mem = navigator.deviceMemory || 0;
    this.modestDevice = this.phoneClass && mem > 0 && mem <= 2;   // reported by Chrome on Android only
    // multisampling stays on everywhere: this room is full of thin lines (blinds, rails, mouldings) that would crawl without it
    try {
      renderer = new T.WebGLRenderer({ canvas: this.canvas, antialias: true, powerPreference: 'high-performance', stencil: false, alpha: false });
    } catch (e) { this.showFallback(); return; }
    this.renderer = renderer;
    this.initQuality();
    renderer.setPixelRatio(this.pixelRatio);
    renderer.debug.checkShaderErrors = false;   // skips a blocking "did it compile?" round trip per shader (a phone stalls on each)
    renderer.outputColorSpace = T.SRGBColorSpace;
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = this.exposure;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.autoUpdate = false;

    this.scene = new T.Scene();
    this.scene.background = new T.Color(this.dataset.background || '#f4efe6');
    this.camera = new T.PerspectiveCamera(55, 1, 0.05, 80);
    this.camera.position.fromArray(this.previewPos);
    this.camera.lookAt(new T.Vector3().fromArray(this.previewTarget));

    this.buildEnvironment();

    var hemi = new T.HemisphereLight(0xfff6ea, 0xcdbba0, HEMI_FULL);
    this.scene.add(hemi);
    this.hemi = hemi;
    if (this.noEnv) {
      // Weak GPUs: no environment map at all (it costs a texture lookup on every pixel). Its soft light is rebuilt from
      // a stronger sky light plus one shadowless fill light from the window side, fitted to the environment's measured
      // brightness in 26 directions.
      hemi.color.set(0xfffefc); hemi.groundColor.set(0xbab7b3); hemi.intensity = HEMI_NOENV;
      var fill = new T.DirectionalLight(0xffffff, FILL_NOENV);
      fill.position.set(1.5, 5, 8.5);
      this.scene.add(fill); this.scene.add(fill.target);
      this.fill = fill;
    }
    var sun = new T.DirectionalLight(0xfff1dc, 2.2);
    sun.position.set(-7, 9, 4);
    sun.castShadow = this.quality.shadows;
    sun.shadow.mapSize.set(this.phoneClass ? 1024 : 2048, this.phoneClass ? 1024 : 2048);
    sun.shadow.bias = -0.0006;
    sun.shadow.normalBias = 0.02;
    var sc = sun.shadow.camera;
    sc.left = -8; sc.right = 8; sc.top = 8; sc.bottom = -8; sc.near = 1; sc.far = 30;
    this.scene.add(sun); this.scene.add(sun.target);
    this.sun = sun;

    this.raycaster = new T.Raycaster();
    this.raycaster.firstHitOnly = true;
    this.pointer = new T.Vector2(-10, -10);
    this.clock = new T.Clock();
    this.tmpV = new T.Vector3(); this.tmpV2 = new T.Vector3(); this.tmpV3 = new T.Vector3();
    this.tmpQ = new T.Quaternion(); this.tmpE = new T.Euler(0, 0, 0, 'YXZ');
    this.spherical = new T.Spherical();
    this.previewTargetV = new T.Vector3().fromArray(this.previewTarget);
    this.spherical.setFromVector3(this.tmpV.fromArray(this.previewPos).sub(this.previewTargetV));
    this.poseTall = false;
    this.peek = { yaw: 0, pitch: 0, mx: 0, my: 0, scroll: 0 };
    this.walk = { pos: new T.Vector3().fromArray(this.walkStart), yaw: 0, pitch: 0, bob: 0 };
    var d = this.tmpV.fromArray(this.walkLook).sub(this.walk.pos);
    this.walk.yaw = Math.atan2(-d.x, -d.z);
    this.walk.pitch = Math.atan2(d.y, Math.hypot(d.x, d.z));
    this.transition = null;
    this.items = [];
    this.allMeshes = [];
    this.collidables = [];
    this.boomBlockers = [];   // what stops the follow camera: the collision meshes plus curtains and blinds (the camera must not end up behind the cloth)
    this.selected = null; this.hovered = null;
    this.pinned = false; this.cardItem = null; this.cardState = null;
    this.markerEls = [];
    this.frameTimes = []; this.lastFrame = 0;

    this.resize();
    this.bindContextLoss();
    this.probeCadence();
    this.bindPointer();
    this.loadModel();
  };

  OakRoom.prototype.resize = function () {
    var w = this.stage.clientWidth || 1, h = this.stage.clientHeight || 1;
    // a narrow stage gets the on-screen arrows, the Walk button inside the picture and the short hints, even with a mouse.
    // Decided before the 3D engine exists, so the page does not shift when it boots (and the no-WebGL fallback gets it too).
    var compact = w < 600;
    if (compact !== this.compact) { this.compact = compact; this.classList.toggle('is-compact', compact); this.setHint(this.hintKind || 'preview'); }
    if (!this.renderer) return;
    var aspect = w / h;
    this.camera.aspect = aspect;
    // The field of view follows the SHAPE of the stage, not the kind of device: a fixed vertical angle turns an upright
    // phone into a keyhole (28 degrees across). Instead the horizontal view never drops below ~58 degrees (66 while
    // walking), up to a vertical limit that keeps the perspective from stretching.
    var wantH = (this.mode === 'walk' ? 66 : 58) * Math.PI / 180;
    var v = 2 * Math.atan(Math.tan(wantH / 2) / aspect) * 180 / Math.PI;
    this.fovTarget = clamp(Math.max(55, v), 55, FOV_MAX);
    if (!this.ready) this.camera.fov = this.fovTarget;   // first layout: no easing (frame() eases later changes)
    this.camera.updateProjectionMatrix();
    this.width = w; this.height = h;
    var rebuilt = this.applyBuffer();
    this.priceItem = null; // the price label's font size depends on the viewport: measure it again
    this.usePreviewPose(aspect < 0.85);
    this.requestFrame();
    // a rebuilt buffer is blank: draw into it right away instead of one frame later (no flash while the phone is turned)
    if (rebuilt && this.ready && !this.contextLost) this.drawNow();
  };
  /* an immediate draw outside the frame loop (after the drawing buffer was rebuilt): overlays use the current camera */
  OakRoom.prototype.drawNow = function () {
    this.camera.updateMatrixWorld(true);
    this.updateOverlays(performance.now());
    this.renderer.render(this.scene, this.camera);
  };
  /* An upright stage uses its own preview camera (theme editor: "Phone camera ..."), a wide one the regular camera. */
  OakRoom.prototype.usePreviewPose = function (tall) {
    if (tall === this.poseTall || !this.spherical) return;
    this.poseTall = tall;
    var pos = tall ? this.previewPosTall : this.previewPos, target = tall ? this.previewTargetTall : this.previewTarget;
    this.previewTargetV.fromArray(target);
    this.spherical.setFromVector3(this.tmpV.fromArray(pos).sub(this.previewTargetV));
  };

  OakRoom.prototype.setProgress = function (f) {
    if (this.ui.bar) this.ui.bar.style.transform = 'scaleX(' + Math.min(0.95, f) + ')';
  };
  /* resolves to the model's bytes, or to null when streaming is not possible (the loader then fetches it itself) */
  OakRoom.prototype.fetchModel = function () {
    var el = this, url = this.dataset.model;
    if (!url || !window.fetch || !window.ReadableStream) return Promise.resolve(null);
    return fetch(url).then(function (res) {
      if (!res.ok || !res.body) throw new Error('no stream');
      var total = parseInt(res.headers.get('content-length'), 10) || 2200000;   // the bundled room is about 2MB
      var reader = res.body.getReader(), chunks = [], got = 0;
      var pump = function () {
        return reader.read().then(function (r) {
          if (r.done) return null;
          chunks.push(r.value); got += r.value.length;
          el.setProgress(0.1 + 0.8 * got / total);
          return pump();
        });
      };
      return pump().then(function () {
        var all = new Uint8Array(got), at = 0;
        chunks.forEach(function (c) { all.set(c, at); at += c.length; });
        return all.buffer;
      });
    }).catch(function () { return null; });
  };
  OakRoom.prototype.loadModel = function () {
    var T = this.T, el = this;
    var loader = new T.GLTFLoader();
    loader.setMeshoptDecoder(T.MeshoptDecoder);
    var onLoad = function (gltf) { el.onModel(gltf); };
    var onError = function (err) { console.error('[oak-room-3d] model failed', err); if (!el.disposed) el.showFallback(); };
    var viaLoader = function () { loader.load(el.dataset.model, onLoad, function (xhr) { if (xhr.total) el.setProgress(xhr.loaded / xhr.total); }, onError); };
    (this.modelBuf || Promise.resolve(null)).then(function (buf) {
      el.modelBuf = null;
      if (el.disposed) return;
      var url = String(el.dataset.model || ''), base = url.slice(0, url.lastIndexOf('/') + 1);   // for models that reference external files
      if (buf) { try { loader.parse(buf, base, onLoad, onError); } catch (e) { viaLoader(); } } else viaLoader();
    });
  };

  OakRoom.prototype.onModel = function (gltf) {
    if (this.disposed || !this.renderer) return;   // removed while the model was still downloading
    var T = this.T, el = this, root = gltf.scene;
    this.model = root;
    this.scene.add(root);
    root.updateMatrixWorld(true);

    // Find "RootNode" (children are the furniture groups).
    var rootNode = null;
    root.traverse(function (n) { if (!rootNode && n.name === 'RootNode') rootNode = n; });
    var groups = rootNode ? rootNode.children : root.children;
    var geoms = new Set();
    var byMatch = {};
    this.hotspots.forEach(function (h) {
      String(h.match || '').split(',').forEach(function (m) { m = normName(m).toLowerCase(); if (m) byMatch[m] = h; });
    });

    this.wallMeshes = [];
    var others = []; // unlinked scenery; small things resting on a linked piece will travel with it
    // iterate over a copy: linked pieces are re-parented under a pivot below, which edits the live children array
    groups.slice().forEach(function (g) {
      var name = normName(g.name);
      var base = baseName(name);
      var meshes = [];
      g.traverse(function (n) {
        if (n.isMesh) {
          meshes.push(n);
          el.allMeshes.push(n);
          n.castShadow = true; n.receiveShadow = true;
          n.matrixAutoUpdate = false;
          geoms.add(n.geometry);
          el.tuneMaterial(n);
        }
      });
      if (!meshes.length) return;
      // The room shell is drawn LAST (then the rug): furniture in front of it is drawn first, so the floor and wall pixels
      // hidden behind furniture are never shaded at all. Measured: 11-15% less GPU time for this room.
      if (base === 'walls') meshes.forEach(function (m) { m.renderOrder = 2; });
      else if (/^carpet/i.test(base)) meshes.forEach(function (m) { m.renderOrder = 1; });
      var isCurtain = /^(curtain|blinds)/i.test(base);
      if (!isCurtain && !/^(carpet)/i.test(base)) meshes.forEach(function (m) { el.collidables.push(m); });
      if (!/^(carpet)/i.test(base)) meshes.forEach(function (m) { el.boomBlockers.push(m); });
      // fixed structure a showcased piece must not swing through
      if (/^(walls|window|door|door_frame|kitchen_cabinets|fridge|stove)$/i.test(base) || /^Cube059$/i.test(name)) meshes.forEach(function (m) { el.wallMeshes.push(m); });
      if (isExcluded(name)) return;
      // Only furniture that is linked to a real store product is interactive.
      // Everything else stays part of the scenery: no hover, no card, no marker.
      var hs = byMatch[name.toLowerCase()] || byMatch[base.toLowerCase()] || null;
      if (!hs) { others.push({ group: g, meshes: meshes, base: base, name: name }); return; }
      var box = new T.Box3().setFromObject(g);
      var center = box.getCenter(new T.Vector3());
      var anchor = new T.Vector3(center.x, box.max.y, center.z);
      // Materials stay SHARED between pieces (fewer state changes per frame, which is what a phone's CPU pays for).
      // A piece only gets its own copies while it glows: see claimGlow / releaseGlow.
      var mats = [];
      var parent = g.parent;
      var p0 = parent.worldToLocal(new T.Vector3(0, 0, 0));
      var p1 = parent.worldToLocal(new T.Vector3(0, 1, 0));
      var localUp = p1.sub(p0);
      // A pivot placed at the piece's own centre: lifting and turning the pivot makes the piece float and
      // spin in place, whatever its original origin was. attach() keeps the world transform unchanged.
      var pivot = new T.Group();
      pivot.name = 'oak-pivot:' + name;
      pivot.position.copy(parent.worldToLocal(center.clone()));
      parent.add(pivot);
      pivot.updateMatrixWorld(true);
      pivot.attach(g);
      var size = box.getSize(new T.Vector3());
      var item = {
        name: name, base: base, group: g, pivot: pivot, meshes: meshes, glowMeshes: meshes.slice(), mats: mats, cloneCache: {}, claimed: false,
        box: box, center: center, anchor: anchor, size: size,
        hotspot: hs, label: (hs && hs.label) || prettyLabel(name), glow: 0, lift: 0, pop: 0,
        basePos: pivot.position.clone(), localUp: localUp, upAxis: localUp.clone().normalize(),
        // showcase state (after a click): show 0..1, angle in radians, how high it floats, and how it may turn
        show: 0, angle: 0, spinVel: 0, liftNow: 0,
        liftHeight: clamp(0.3 + size.y * 0.1, 0.3, 0.46), spinMode: 'full', swayAmp: 0.16,
        // clearance plan (filled by ensureClearance on first click): a horizontal slide away from neighbours / walls
        cleared: false, slideWorld: new T.Vector3(), slideLocal: new T.Vector3(), slideNow: new T.Vector3(),
        onFloor: box.min.y < 0.25
      };
      el.items.push(item);
      meshes.forEach(function (m) { m.userData.oakItem = item; });
    });

    // Riders: cushions on the sofa, bowls on the table, the sink and faucet in the island, plants on a nightstand...
    // They are separate objects in the model, so they are re-parented to the piece they rest on and float with it.
    // (A wall-mounted TV is rejected by the height test below; a TV standing on its console rides with it.)
    var notRiders = /^(light|Cylinder|curtain|blinds|rail|painting|painting_2|painting_3|mirror|carpet)$/i;
    this.obstacles = []; // free-standing scenery a floating piece should keep clear of (coffee table, fridge, plants...)
    others.forEach(function (o) {
      if (notRiders.test(o.base)) return;
      var ob = new T.Box3().setFromObject(o.group);
      var oc = ob.getCenter(new T.Vector3()), os = ob.getSize(new T.Vector3());
      var best = null, bestArea = Infinity, bestOnTop = false;
      el.items.forEach(function (it) {
        var b = it.box;
        if (oc.x < b.min.x - 0.04 || oc.x > b.max.x + 0.04 || oc.z < b.min.z - 0.04 || oc.z > b.max.z + 0.04) return; // not over its footprint
        if (ob.min.y < b.min.y + 0.1 * it.size.y || ob.min.y > b.max.y + 0.15) return;                                // not resting on / set into it
        // a bowl sits ON the table top even though a chair is tucked under it: "resting on the top surface" wins,
        // and a leafy plant standing on a console may be wider than the console itself
        var onTop = Math.abs(ob.min.y - b.max.y) < 0.05;
        if (!onTop && os.x * os.z >= it.size.x * it.size.z) return;                                                   // other riders are smaller than their carrier
        var area = it.size.x * it.size.z;
        if ((onTop && !bestOnTop) || (onTop === bestOnTop && area < bestArea)) { best = it; bestArea = area; bestOnTop = onTop; }
      });
      if (!best) { if (os.y > 0.12) el.obstacles.push({ box: ob, name: o.name }); return; }
      best.pivot.attach(o.group);
      best.riders = (best.riders || 0) + 1;
      o.meshes.forEach(function (m) {
        m.userData.oakItem = best; // hovering a cushion counts as hovering the sofa
        best.glowMeshes.push(m);   // riders glow with their carrier
      });
    });
    // entry-level GPUs start on the cheaper shading; the physically based shaders are then never even compiled
    if (this.quality.lite) this.applyShading(true);

    // Nothing in this room moves on its own: every node's matrix is computed once, here, and three.js is told not to
    // walk all ~600 nodes again on every frame. Whatever does move updates itself (pivots in frame(), the glow ring).
    this.scene.updateMatrixWorld(true);
    root.traverse(function (n) { n.matrixAutoUpdate = false; });
    this.scene.matrixWorldAutoUpdate = false;

    // How each piece may move while showcased is decided lazily, on its first click (see ensureClearance).
    this.buildShowcaseRing();
    // warm up the price font so the first price never flashes in a fallback face; re-measure the label once it is in
    if (this.items.length && document.fonts && document.fonts.load) {
      document.fonts.load('400 24px "Anton"').then(function () { el.priceItem = null; }).catch(function () { /* fallback font is fine */ });
    }

    // scene bounds for walk clamping
    this.bounds = new T.Box3().setFromObject(root);
    this.walkMin = new T.Vector3(this.bounds.min.x + 0.35, 0, this.bounds.min.z + 0.35);
    this.walkMax = new T.Vector3(this.bounds.max.x - 0.35, 0, this.bounds.max.z - 0.35);

    // shadows: render once (static scene)
    this.renderer.shadowMap.needsUpdate = true;
    this.buildMarkers();

    var started = false;
    var start = function () {
      if (started || el.disposed) return;
      started = true;
      el.buildBvh(Array.from(geoms));
      if (el.ui.bar) el.ui.bar.style.transform = 'scaleX(1)';
      el.classList.add('is-ready');
      el.setWalkButtons(false);
      el.ready = true;
      el.running = true;
      el.readyAt = performance.now();
      el.shadowsLive = !!(el.sun && el.sun.castShadow);
      if (/[?&]oak3d=debug\b/.test(window.location.search)) el.showDebug();
      el.setHint(el.hintKind || 'preview');   // restarts the hint's timer now that the room is actually visible
      el.clock.start();
      el.startLoop();
      el.dispatchEvent(new CustomEvent('oak:ready', { bubbles: true }));
    };
    // The shaders are compiled BEFORE the room is revealed and, where the driver supports it, off the main thread:
    // a cheap phone used to freeze for seconds on its first frame. Then the 13 textures are sent to the GPU one at a time
    // (each is a 10-30ms job on a cheap phone) instead of all inside the first frame. The poster stays up meanwhile.
    var warm = function () {
      var maps = [], seen = {};
      el.allMeshes.forEach(function (m) { var tex = m.material && m.material.map; if (tex && !seen[tex.uuid]) { seen[tex.uuid] = true; maps.push(tex); } });
      var next = function () {
        if (started || el.disposed) return;
        var tex = maps.shift();
        if (!tex) { start(); return; }
        try { el.renderer.initTexture(tex); } catch (e) { /* uploaded on first use instead */ }
        setTimeout(next, 0);
      };
      next();
    };
    try { this.renderer.compileAsync(this.scene, this.camera).then(warm, warm); } catch (e) { start(); }
    setTimeout(start, 6000);   // never wait on a driver that does not answer
  };

  /*
   * Every material is tuned ONCE and stays shared by all meshes that use it (the loader already shares them).
   * They are kept as the model defines them: in three.js r186 a MeshPhysicalMaterial without clearcoat / sheen /
   * transmission compiles to the same shader as MeshStandardMaterial, so converting would only change the look.
   */
  OakRoom.prototype.tuneMaterial = function (mesh) {
    var mat = mesh.material;
    if (!mat) return;
    mesh.userData.matFull = mat;
    this.tuned = this.tuned || {};
    if (!this.tuned[mat.uuid]) {
      this.tuned[mat.uuid] = true;
      var name = (mat.name || '').toLowerCase();
      if (mat.map) {
        // big surfaces seen at a shallow angle (floor, rug, counters, table tops) need strong anisotropic filtering or
        // their grain dissolves into a flat colour a few metres away, which is exactly what reads as "no texture" on a phone.
        // 8x covers the whole 11m room from eye height; small or upright surfaces get by with less on a weak GPU.
        // (16x never pays here: from eye height the footprint only stretches 8x at 12.7m, the room is 11m long)
        var maxAniso = this.renderer.capabilities.getMaxAnisotropy();
        var want = /^(floor|carpet)$/.test(name) ? 8 : name === 'countertop' ? 4 : /^(wood|details|backsplash)$/.test(name) ? (this.noEnv ? 2 : 4) : name === 'white_fabric' ? 2 : 1;
        // textures are shared between materials (both cane materials use one image): the higher request wins
        mat.map.anisotropy = Math.min(maxAniso, Math.max(mat.map.anisotropy || 1, want));
      }
      // the oak floor is the biggest textured surface: kept satin, not glossy, so window reflections never bleach its grain
      if (name === 'floor') mat.roughness = Math.max(mat.roughness, 0.7);
      if (name === 'glass') { mat.transparent = true; mat.opacity = 0.28; mat.depthWrite = false; mat.roughness = 0.05; mat.metalness = 0; }
      if (name === 'mirror') { mat.metalness = 1; mat.roughness = 0.04; }
      if (name === 'glow_warm') { mat.emissive = new this.T.Color(0xffc37a); mat.emissiveIntensity = 1.6; }
      // the curtain is flagged as a cut-out in the file but has no texture to cut with: a "discard" that can never fire
      // only switches off the GPU's early depth rejection for its 33k triangles
      if (name === 'curtain') { mat.side = this.T.DoubleSide; mat.alphaTest = 0; }
      if (name === 'mesh_white' || name === 'mesh_black' || name === 'fern') {
        // real cut-out textures (cane, fern leaves): the file marks them as blended, which also switches depth writing
        // off; as cut-outs they must write depth again, or the floor, which is drawn last, would paint over them
        mat.alphaTest = 0.4; mat.transparent = false; mat.depthWrite = true; mat.side = this.T.DoubleSide;
      }
      // the other plants' images have no alpha channel at all: plain opaque, double-sided leaves
      if (name === 'strelitzia' || name === 'succulent' || name === 'broadleaf') { mat.alphaTest = 0; mat.transparent = false; mat.depthWrite = true; mat.side = this.T.DoubleSide; }
      // Small shiny things take their reflections from the environment map EXPLICITLY, so they keep them in the
      // environment-free shading of weak GPUs (where scene.environment is off), and their strength can be set per material.
      if (this.isHero(mat)) {
        mat.envMap = this.envTex; mat.envMapIntensity = name === 'mirror' ? 1.15 : 1;
        (this.heroMats || (this.heroMats = [])).push(mat);
        if (this.noEnv) {   // there the sky light already lights them once: no second helping of ambient light
          if (name === 'brass') mat.metalness = 1;
          if (name === 'glass') mat.color.multiplyScalar(0.5);
        }
      }
    }
    this.tuneMesh(mesh, mat);
  };
  OakRoom.prototype.isHero = function (mat) {
    var name = (mat.name || '').toLowerCase();
    return mat.metalness > 0.3 || mat.transparent || name === 'mirror' || name === 'glass';
  };  /* per mesh: what does not need to throw a shadow (glass, lamps, curtains, the room shell itself) */
  OakRoom.prototype.tuneMesh = function (mesh, mat) {
    var name = (mat.name || '').toLowerCase();
    if (name === 'glass' || name === 'glow_warm' || name === 'curtain' || name === 'walls' || name === 'floor') mesh.castShadow = false;
  };

  /* BVH built in idle time so the intro stays smooth; anything left is finished when walking starts */
  OakRoom.prototype.buildBvh = function (geoms) {
    var el = this;
    var tris = function (g) { return (g.index ? g.index.count : g.attributes.position.count) / 3; };
    // Small geometries (two thirds of them, 13k triangles in all) never get a tree: testing their few triangles directly
    // costs microseconds. The big ones come first, so picking and walking are fast as early as possible.
    var queue = geoms.filter(function (g) { return tris(g) >= 300; }).sort(function (a, b) { return tris(b) - tris(a); });
    this.bvhQueue = queue;
    var step = function (deadline) {
      if (el.disposed) return;
      var t0 = performance.now();
      while (queue.length && (deadline ? deadline.timeRemaining() > 4 : performance.now() - t0 < 6)) {
        var g = queue.shift();
        if (!g.boundsTree) { try { g.computeBoundsTree(); } catch (e) { /* ignore */ } }
      }
      if (!queue.length) { el.bvhReady = true; el.armGovernor(); return; }
      // in walk mode the rest is built in short slices right away instead of waiting for idle time
      if (el.mode === 'walk' || !window.requestIdleCallback) el.bvhHandle = setTimeout(function () { step(null); }, 16);
      else el.bvhHandle = window.requestIdleCallback(step, { timeout: 1500 });
    };
    step(null);
  };
  OakRoom.prototype.finishBvh = function () {
    var q = this.bvhQueue || [];
    while (q.length) { var g = q.shift(); if (!g.boundsTree) { try { g.computeBoundsTree(); } catch (e) { /* ignore */ } } }
    this.bvhReady = true;
    this.armGovernor();
  };

  /* ---------- hotspot markers (pulsing dots on mapped products) ---------- */
  OakRoom.prototype.buildMarkers = function () {
    var el = this;
    if (!this.ui.markers) return;
    this.ui.markers.innerHTML = '';
    this.markerEls = [];
    this.items.forEach(function (it) {
      if (!it.hotspot) return;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'oak-marker';
      b.setAttribute('aria-label', it.label);
      // built with DOM APIs: product titles are never parsed as HTML
      var dot = document.createElement('span'); dot.className = 'oak-marker__dot';
      var lab = document.createElement('span'); lab.className = 'oak-marker__label'; lab.textContent = it.label;
      b.appendChild(dot); b.appendChild(lab);
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        el.activate(it);
        // keyboard activation (detail === 0): once the category face shows, move focus to its button
        if (e.detail === 0 && el.cardState === 'category' && el.ui.card) {
          var cta = el.ui.card.querySelector('[data-oak-card-cat-link]');
          if (cta && !cta.hidden) setTimeout(function () { cta.focus({ preventScroll: true }); }, 60);
        }
      });
      b.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') el.setHover(it); });
      b.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') el.setHover(null); });
      el.ui.markers.appendChild(b);
      el.markerEls.push({ el: b, item: it, visible: false, lastCheck: 0 });
    });
  };

  /* ---------- pointer: hover / click / drag-look ---------- */
  OakRoom.prototype.bindPointer = function () {
    var el = this, c = this.canvas;
    this.drag = null;
    c.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      el.lastPointerType = e.pointerType || 'mouse';
      // a second finger never takes over the look-around, and lifting it is not a tap on the furniture
      if (el.drag && el.drag.id !== e.pointerId) { el.drag.multi = true; return; }
      el.drag = { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, moved: false, id: e.pointerId, t: performance.now() };
      // capture every drag so a release outside the stage still ends it
      try { c.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      el.hovering = true;
    });
    c.addEventListener('lostpointercapture', function (e) { if (el.drag && el.drag.id === e.pointerId) el.drag = null; });
    c.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse' || el.drag) return;
      var to = e.relatedTarget;
      if (to && ((el.ui.markers && el.ui.markers.contains(to)) || (el.ui.card && el.ui.card.contains(to)))) return; // onto a marker or the card
      el.pointer.set(-10, -10);
      el.pointerDirty = false;
      el.setHover(null);
    });
    c.addEventListener('pointermove', function (e) {
      el.lastPointerType = e.pointerType || 'mouse';
      if (el.drag && e.pointerType === 'mouse' && e.buttons === 0) el.drag = null; // safety net for a missed pointerup
      el.updatePointer(e);
      if (el.drag && el.drag.id === e.pointerId) {
        var dx = e.clientX - el.drag.x, dy = e.clientY - el.drag.y;
        // a fingertip wobbles more than a mouse: up to 12px is still a tap (6px with a mouse)
        if (Math.abs(e.clientX - el.drag.sx) + Math.abs(e.clientY - el.drag.sy) > (e.pointerType === 'touch' ? 12 : 6)) el.drag.moved = true;
        if (el.drag.moved) {
          if (el.mode === 'walk' && e.pointerType === 'touch') {
            // a finger grabs the room and drags it (like a street-level photo viewer); the gain follows the field of
            // view, so the spot under the finger stays under the finger
            var gain = 1.4 * (el.camera.fov * Math.PI / 180) / Math.max(1, el.height);
            el.walk.yaw += dx * gain;
            el.walk.pitch = clamp(el.walk.pitch + dy * gain, -1.1, 1.1);
          } else if (el.mode === 'walk') {
            el.walk.yaw -= dx * 0.0042;
            el.walk.pitch = clamp(el.walk.pitch - dy * 0.0032, -1.1, 1.1);
          } else {
            el.peek.yaw = clamp(el.peek.yaw - dx * 0.0022, -0.55, 0.55);
            el.peek.pitch = clamp(el.peek.pitch + dy * 0.0016, -0.25, 0.2);
          }
        }
        el.drag.x = e.clientX; el.drag.y = e.clientY;
        el.requestFrame();
      } else if (e.pointerType === 'mouse' && el.mode === 'preview') {
        el.peek.mx = (e.clientX / window.innerWidth - 0.5) * 2;
        el.peek.my = (e.clientY / window.innerHeight - 0.5) * 2;
        el.requestFrame();
      }
      el.pointerDirty = true;
      if (e.pointerType === 'mouse' && el.asleep) el.queueFrame();
    });
    var end = function (e) {
      if (!el.drag || el.drag.id !== e.pointerId) return;
      var wasClick = !el.drag.moved && !el.drag.multi && performance.now() - el.drag.t < 700;
      el.drag = null;
      if (wasClick) { el.updatePointer(e); el.pick(true); }
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', function () { el.drag = null; });
    c.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    c.addEventListener('wheel', function (e) { if (el.mode === 'walk') e.preventDefault(); }, { passive: false });
  };
  OakRoom.prototype.updatePointer = function (e) {
    var r = this.canvas.getBoundingClientRect();
    this.pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    this.pointer.y = -((e.clientY - r.top) / r.height) * 2 + 1;
    this.pointerInside = this.pointer.x >= -1 && this.pointer.x <= 1 && this.pointer.y >= -1 && this.pointer.y <= 1;
  };
  OakRoom.prototype.pick = function (isClick) {
    if (!this.ready) return;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    this.raycaster.far = 40;
    var hits = this.raycaster.intersectObjects(this.allMeshes, false);
    var item = hits.length && hits[0].object.userData.oakItem ? hits[0].object.userData.oakItem : null;
    if (isClick) this.activate(item); else this.setHover(item);
  };

  /*
   * Card behaviour
   *   hover (mouse)      -> "peek" face: product name + price
   *   click / tap        -> first shows the peek face (if it is not showing yet), the next click flips the card
   *                         to the "category" face: the product's category + a button to the whole category
   *   click again / away -> closes
   */
  OakRoom.prototype.setHover = function (item) {
    // same piece again: nothing to do unless its card was closed in the meantime
    if (item === this.hovered && (!item || this.pinned || this.cardItem === item)) return;
    this.hovered = item;
    this.canvas.style.cursor = item ? 'pointer' : (this.mode === 'walk' ? 'grab' : 'default');
    if (this.pinned) return; // a clicked card stays until it is dismissed
    if (item) this.showCard(item, 'peek'); else this.hideCard();
    this.requestFrame();
  };
  OakRoom.prototype.activate = function (item) {
    if (!item) { this.select(null); return; }
    this.ensureClearance(item); // plan how this piece may float and turn (once)
    if (this.cardItem === item && this.cardState === 'peek') {
      this.pinned = true; this.selected = item; item.pop = 1;
      this.showCard(item, 'category');
    } else if (this.cardItem === item && this.cardState === 'category') {
      this.pinned = false; this.selected = null;
      if (this.hovered === item && this.lastPointerType === 'mouse') this.showCard(item, 'peek'); else this.hideCard();
    } else {
      if (this.selected && this.selected !== item) this.selected.pop = 0;
      this.pinned = true; this.selected = item; item.pop = 1;
      this.showCard(item, 'peek');
    }
    this.requestFrame();
    this.dispatchEvent(new CustomEvent('oak:select', { bubbles: true, detail: { name: item.name, label: item.label, state: this.cardState, hotspot: item.hotspot } }));
  };
  /* Programmatic selection (theme editor block select, close button, mode changes). */
  OakRoom.prototype.select = function (item) {
    if (!item) {
      this.pinned = false; this.selected = null;
      // forget the hover too, so moving back onto the same piece shows its card again
      this.hovered = null;
      if (this.canvas) this.canvas.style.cursor = this.mode === 'walk' ? 'grab' : 'default';
      this.hideCard(); this.requestFrame();
      return;
    }
    this.ensureClearance(item);
    this.pinned = true; this.selected = item; item.pop = 1;
    this.showCard(item, 'category');
    this.requestFrame();
  };
  OakRoom.prototype.hideCard = function () {
    var card = this.ui.card, el = this;
    this.cardItem = null; this.cardState = null;
    if (!card) return;
    card.classList.remove('is-open', 'is-pinned');
    card.setAttribute('aria-hidden', 'true');
    // once faded out, fall back to the harmless peek face so nothing clickable lingers over the canvas
    clearTimeout(this.cardResetTimer);
    this.cardResetTimer = setTimeout(function () { if (!el.cardItem) card.setAttribute('data-state', 'peek'); }, 260);
  };
  OakRoom.prototype.showCard = function (item, state) {
    var card = this.ui.card;
    if (!card) return;
    var hs = item.hotspot || {};
    var q = function (sel) { return card.querySelector(sel); };
    if (this.cardItem !== item) {
      var title = q('[data-oak-card-title]'), price = q('[data-oak-card-price]'), compare = q('[data-oak-card-compare]');
      var img = q('[data-oak-card-img]'), cat = q('[data-oak-card-category]'), catLink = q('[data-oak-card-cat-link]'), link = q('[data-oak-card-link]');
      if (title) title.textContent = item.label;
      if (price) { price.textContent = hs.price || ''; price.hidden = !hs.price; }
      if (compare) { compare.textContent = hs.compare || ''; compare.hidden = !hs.compare; }
      if (img) { if (hs.image) { img.src = hs.image; img.alt = item.label; img.hidden = false; } else { img.hidden = true; img.removeAttribute('src'); } }
      if (cat) cat.textContent = hs.category || '';
      if (catLink) { if (hs.category_url) { catLink.href = hs.category_url; catLink.hidden = false; } else { catLink.hidden = true; } }
      if (link) { if (hs.url) { link.href = hs.url; link.hidden = false; } else { link.hidden = true; } }
      // the card jumps to another piece: switch faces without the flip, then restart the entrance animation
      card.classList.add('is-instant');
      card.setAttribute('data-state', state);
      card.classList.remove('is-open'); void card.offsetWidth;
      card.classList.remove('is-instant');
    }
    clearTimeout(this.cardResetTimer);
    this.cardItem = item; this.cardState = state;
    card.setAttribute('data-state', state);
    card.classList.toggle('is-pinned', !!this.pinned);
    card.classList.add('is-open');
    card.setAttribute('aria-hidden', 'false');
    this.cardHeight = card.offsetHeight; // cached: read once per change, not per frame
  };

  /* ---------- walk mode ---------- */
  OakRoom.prototype.enterWalk = function () {
    if (!this.ready || this.mode !== 'preview' || this.transition) return;
    var T = this.T, el = this;
    // no synchronous "finish the trees now": on a slow phone that froze the tap for up to 300ms. Rays work without a
    // tree (just slower), nobody walks during the 1.4s fly-in, and buildBvh speeds up once the mode is 'walk'.
    this.mode = 'walk';
    this.classList.add('is-walking');
    document.documentElement.classList.add('oak-walk-lock');
    this.stage.addEventListener('touchmove', this.onWalkTouchMove, { passive: false });
    // the phone's Back button should close the walk, not leave the shop (not inside the theme editor's preview)
    if (!(window.Shopify && window.Shopify.designMode)) {
      try { window.history.pushState({ oakWalk: 1 }, ''); this.pushedState = true; } catch (e) { this.pushedState = false; }
    }
    this.setHint('walk');
    this.select(null);
    this.keys = {};
    this.resize();   // the stage is fullscreen now: its shape decides the field of view
    // sideways phones put the view switch BESIDE Exit: it needs Exit's real width (translated labels, other fonts)
    if (this.ui.viewBtn && this.ui.exitBtn) this.style.setProperty('--oak-exit-w', this.ui.exitBtn.offsetWidth + 'px');
    this.measureBottomUi();
    setTimeout(function () { if (el.mode === 'walk') el.measureBottomUi(); }, 600);
    // the avatar for the third-person view is fetched now, quietly, so the switch is instant when the button is pressed
    this.view = 'first'; this.tpBlend = 0; this.updateViewButton();
    if (this.dataset.person) setTimeout(function () { if (el.mode === 'walk' && !el.disposed) el.loadPerson().catch(function () { /* the button will try again */ }); }, 1600);
    // an upright phone sees a lot of ceiling with its tall field of view: start with the eyes a little lower
    if (this.camera.aspect < 0.85) this.walk.pitch = Math.min(this.walk.pitch, -0.2);
    var from = { p: this.camera.position.clone(), q: this.camera.quaternion.clone() };
    this.tmpE.set(this.walk.pitch, this.walk.yaw, 0, 'YXZ');
    var to = { p: this.walk.pos.clone(), q: new T.Quaternion().setFromEuler(this.tmpE) };
    this.transition = { from: from, to: to, t: 0, dur: this.reduceMotion ? 0.01 : 1.4, done: function () { el.canvas.style.cursor = 'grab'; } };
    this.canvas.focus && this.canvas.focus({ preventScroll: true });
    this.requestFrame();
    this.dispatchEvent(new CustomEvent('oak:walk', { bubbles: true, detail: { walking: true } }));
  };
  /* Whatever the page pins to the bottom of the screen on top of everything (Shopify's theme preview bar, a cookie bar,
     a chat bubble) would sit right on the on-screen arrows: measure it and lift the arrows above it (--oak-ui-bottom). */
  OakRoom.prototype.measureBottomUi = function () {
    var H = window.innerHeight, W = window.innerWidth, cover = 0, xs = [0.5, 0.25, 0.75];
    try {
      for (var i = 0; i < xs.length; i++) {
        var n = document.elementFromPoint(W * xs[i], H - 6);
        if (!n || this.contains(n)) continue;
        // climb to the element that is actually pinned
        var p = n, box = null;
        while (p && p !== document.body && p !== document.documentElement) {
          var pos = window.getComputedStyle(p).position;
          if (pos === 'fixed' || pos === 'sticky') { box = p.getBoundingClientRect(); break; }
          p = p.parentElement;
        }
        if (!box) box = n.getBoundingClientRect();
        if (box.top > H * 0.55 && box.height < H * 0.45) cover = Math.max(cover, H - box.top);
      }
    } catch (e) { /* ignore */ }
    this.style.setProperty('--oak-ui-bottom', Math.round(cover) + 'px');
  };

  /* leaving = true when the page itself is going away (pagehide): no history juggling then */
  OakRoom.prototype.exitWalk = function (leaving) {
    if (this.mode !== 'walk') return;
    var el = this;
    this.mode = 'preview';
    this.classList.remove('is-walking');
    document.documentElement.classList.remove('oak-walk-lock');
    this.stage.removeEventListener('touchmove', this.onWalkTouchMove);
    // closed with the Exit button or Esc: take back the history entry that walk mode added for the Back button
    if (this.pushedState && leaving !== true) { this.pushedState = false; try { window.history.back(); } catch (e) { /* ignore */ } }
    this.pushedState = false;
    if (this.releasePad) this.releasePad();
    // back to the visitor's own eyes; the avatar leaves the room
    this.view = 'first'; this.tpBlend = 0; this.tpBusy = false; this.tpIdle = false; this.tpLastX = this.tpLastZ = undefined;
    this.classList.remove('is-third');
    if (this.person) { this.person.root.visible = false; this.person.blob.visible = false; }
    this.updateViewButton();
    this.setHint('preview');
    this.select(null);
    this.keys = {};
    this.walk.pitch = clamp(this.walk.pitch, -0.6, 0.6);
    this.resize();   // first: the stage is back to its preview shape, which decides the preview camera and field of view
    var from = { p: this.camera.position.clone(), q: this.camera.quaternion.clone() };
    this.computePreviewPose(this.tmpV, this.tmpQ, 0);
    var to = { p: this.tmpV.clone(), q: this.tmpQ.clone() };
    this.transition = { from: from, to: to, t: 0, dur: this.reduceMotion ? 0.01 : 1.1, done: function () { el.canvas.style.cursor = 'default'; } };
    this.requestFrame();
    this.dispatchEvent(new CustomEvent('oak:walk', { bubbles: true, detail: { walking: false } }));
  };

  OakRoom.prototype.computePreviewPose = function (outPos, outQuat, time) {
    var T = this.T;
    var sway = this.reduceMotion ? 0 : Math.sin(time * 0.35) * 0.035 * (this.swayGain === undefined ? 1 : this.swayGain);
    var yaw = this.peek.yaw + sway + this.peek.mx * -0.05 + this.peek.scroll * 0.12;
    var pitch = this.peek.pitch + this.peek.my * 0.03 + this.peek.scroll * 0.08;
    var s = this.spherical;
    var phi = clamp(s.phi + pitch, 0.35, Math.PI / 2 - 0.05);
    var theta = s.theta + yaw;
    var radius = s.radius * (1 + this.peek.scroll * 0.18);
    var sinPhi = Math.sin(phi);
    outPos.set(radius * sinPhi * Math.sin(theta), radius * Math.cos(phi), radius * sinPhi * Math.cos(theta)).add(this.previewTargetV);
    outPos.y += this.peek.scroll * 0.6;
    // reused objects: nothing is allocated per frame (garbage collection pauses show up as dropped frames on cheap phones)
    var m = (this.tmpM || (this.tmpM = new T.Matrix4())).lookAt(outPos, this.previewTargetV, this.upV || (this.upV = new T.Vector3(0, 1, 0)));
    outQuat.setFromRotationMatrix(m);
  };

  OakRoom.prototype.updateScrollPeek = function () {
    if (this.mode !== 'preview') return;
    var r = this.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    // 0 when the section top is at the top of the viewport, grows as it scrolls away
    var t = clamp(-r.top / Math.max(1, r.height), -0.5, 1);
    this.peek.scroll = this.phoneClass ? 0 : t;   // phones keep the room still while the page scrolls (see onScroll)
  };

  /* ---------- movement with collision ---------- */
  OakRoom.prototype.moveWalker = function (dt) {
    var k = this.keys, w = this.walk, T = this.T;
    var fwd = (k.up || k.w ? 1 : 0) - (k.down || k.s ? 1 : 0);
    var turn = (k.left ? 1 : 0) - (k.right ? 1 : 0);
    var strafe = (k.d ? 1 : 0) - (k.a ? 1 : 0);
    var moving = fwd !== 0 || strafe !== 0;
    if (turn) w.yaw += turn * 1.5 * dt;
    if (!moving) { w.bob *= Math.max(0, 1 - dt * 6); return false; }
    var speed = (k.shift ? 3.2 : 1.9) * dt;
    var dir = this.tmpV.set(-Math.sin(w.yaw) * fwd + Math.cos(w.yaw) * strafe, 0, -Math.cos(w.yaw) * fwd - Math.sin(w.yaw) * strafe);
    if (dir.lengthSq() > 1) dir.normalize();
    var delta = dir.multiplyScalar(speed);
    this.tryMove(delta);
    w.bob += dt * 9;
    return true;
  };
  OakRoom.prototype.tryMove = function (delta) {
    var w = this.walk, T = this.T;
    var len = delta.length();
    if (len < 1e-6) return;
    var dir = this.tmpV2.copy(delta).divideScalar(len);
    var hit = this.castMove(w.pos, dir, len + 0.42);
    if (!hit) {
      // the spot under a floating piece stays off-limits (the collision rays pass beneath it while it is up)
      if (!this.underFloating(w.pos.x + delta.x, w.pos.z + delta.z, w.pos.x, w.pos.z)) w.pos.add(delta);
    }
    else {
      var n = hit; n.y = 0;
      if (n.lengthSq() > 1e-6) {
        n.normalize();
        var slide = this.tmpV3.copy(delta).sub(n.multiplyScalar(delta.dot(n)));
        var sl = slide.length();
        if (sl > 1e-5) {
          var sdir = slide.clone().divideScalar(sl);
          if (!this.castMove(w.pos, sdir, sl + 0.42) && !this.underFloating(w.pos.x + slide.x, w.pos.z + slide.z, w.pos.x, w.pos.z)) w.pos.add(slide);
        }
      }
    }
    w.pos.x = clamp(w.pos.x, this.walkMin.x, this.walkMax.x);
    w.pos.z = clamp(w.pos.z, this.walkMin.z, this.walkMax.z);
  };
  var HEIGHTS = [1.45, 0.75, 0.3];
  OakRoom.prototype.castMove = function (pos, dir, dist) {
    var rc = this.raycaster;
    rc.far = dist;
    for (var i = 0; i < HEIGHTS.length; i++) {
      rc.ray.origin.set(pos.x, HEIGHTS[i], pos.z);
      rc.ray.direction.copy(dir);
      var hits = rc.intersectObjects(this.collidables, false);
      if (hits.length) {
        var h = hits[0];
        var n = new this.T.Vector3(0, 0, 1);
        if (h.face) n.copy(h.face.normal).transformDirection(h.object.matrixWorld);
        if (n.dot(dir) > 0) n.negate();
        return n;
      }
    }
    return null;
  };

  /* ---------- render loop ---------- */
  OakRoom.prototype.startLoop = function () {
    var el = this;
    this.frameReq = false;
    this.visible = true;
    if (!this.visIo) {
      this.visIo = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        el.visible = e.isIntersecting && e.intersectionRatio >= 0.15;   // a sliver at the screen edge is not worth a GPU
        if (el.visible) el.requestFrame();
      }, { threshold: [0, 0.15] });
      this.visIo.observe(this);
    }
    this.requestFrame();
  };
  /* requestFrame = "something changed, draw it"; queueFrame only keeps the loop turning (see the end of frame()) */
  OakRoom.prototype.requestFrame = function () {
    this.dirty = true;
    this.lastInputAt = performance.now();
    this.queueFrame();
  };
  OakRoom.prototype.queueFrame = function () {
    if (this.frameReq || !this.ready) return;
    this.frameReq = true;
    var el = this;
    requestAnimationFrame(function (t) { el.frameReq = false; el.frame(t); });
  };
  OakRoom.prototype.frame = function (now) {
    if (!this.renderer || !this.ready) return;
    // wake up on visibility / intersection / context restore. A running camera move always finishes, even with the stage
    // mostly scrolled away (otherwise "Walk around" would stay blocked by a frozen fly-back).
    if (this.pageHidden || this.contextLost || (!this.visible && this.mode !== 'walk' && !this.transition)) return;
    if (this.lastTickAt && !this.drewLastTick) this.noteCadence(now - this.lastTickAt);   // the screen's own rhythm, GPU idle
    this.lastTickAt = now;
    var dt = Math.min(0.05, this.clock.getDelta());
    var time = this.clock.elapsedTime;
    var T = this.T, cam = this.camera;

    if (this.scrollDirty) { this.scrollDirty = false; this.updateScrollPeek(); }
    // The slow idle sway keeps a desktop preview alive. On a phone it rests after 20s without input (and always in lite
    // quality), so an unattended phone stops drawing altogether; any touch, key or scroll wakes it again.
    var swayOn = !this.phoneClass || (!(this.quality && this.quality.lite) && now - (this.lastInputAt || 0) < 20000);
    var sg = this.swayGain === undefined ? 1 : this.swayGain;
    sg += ((swayOn ? 1 : 0) - sg) * Math.min(1, dt * 1.2);
    this.swayGain = sg < 0.002 ? 0 : sg;
    // keyboard peek in preview mode
    if (this.mode === 'preview') {
      var k = this.keys;
      var py = (k.left ? 1 : 0) - (k.right ? 1 : 0), pp = (k.up ? 1 : 0) - (k.down ? 1 : 0);
      if (py) this.peek.yaw = clamp(this.peek.yaw + py * 0.9 * dt, -0.55, 0.55);
      if (pp) this.peek.pitch = clamp(this.peek.pitch - pp * 0.5 * dt, -0.25, 0.2);
      // gentle return to centre, but never under a finger or while a card is open: the piece the shopper turned
      // to would slide away; on phones it waits 3s after the last touch
      if (!this.drag && !this.pinned && (!this.phoneClass || now - (this.lastInputAt || 0) > 3000)) {
        this.peek.yaw *= 1 - dt * 0.35; this.peek.pitch *= 1 - dt * 0.35;
        if (Math.abs(this.peek.yaw) < 1e-4) this.peek.yaw = 0;
        if (Math.abs(this.peek.pitch) < 1e-4) this.peek.pitch = 0;
      }
    }

    if (this.transition) {
      var tr = this.transition;
      tr.t += dt / tr.dur;
      var e = easeInOut(clamp(tr.t, 0, 1));
      cam.position.lerpVectors(tr.from.p, tr.to.p, e);
      cam.quaternion.slerpQuaternions(tr.from.q, tr.to.q, e);
      if (tr.t >= 1) { this.transition = null; this.dirty = true; if (tr.done) tr.done(); }   // dirty: the landing frame is drawn
    } else if (this.mode === 'walk') {
      var walked = this.moveWalker(dt);
      this.updateWalkCamera(dt, time, walked);   // first person, third person, or the glide between them
    } else {
      this.computePreviewPose(cam.position, cam.quaternion, time);
    }

    // hover raycast (only when BVH is ready — cheap), throttled to pointer movement
    if (this.pointerDirty && this.bvhReady && this.items.length && this.lastPointerType === 'mouse' && !this.drag) { this.pointerDirty = false; this.pick(false); }

    // item animations: glow + small lift on hover; after a click the piece is "showcased":
    // it floats up, turns slowly in place (or sways when a wall is close) and its price appears beneath it
    var lift = this.tmpLift || (this.tmpLift = new T.Vector3());
    var TWO_PI = Math.PI * 2, showcasing = false, glowBusy = false;
    for (var i = 0; i < this.items.length; i++) {
      var it = this.items[i];
      var isShown = it === this.selected;
      var targetGlow = (it === this.hovered ? 0.55 : 0) + (isShown ? 0.3 : 0);
      var glowMoving = Math.abs(it.glow - targetGlow) > 0.002;
      if (!glowMoving && !(it.pop > 0) && !isShown && it.show === 0) {   // at rest: nothing to do
        // a piece that is still hovered keeps its glowing copies; only a piece nobody points at hands them back.
        // These clean-ups change the picture, so the frame counts as busy: it is drawn, and a phone does not fall asleep on it.
        if (it.claimed && targetGlow === 0) { this.releaseGlow(it); glowBusy = true; }
        if (it.shadowOff) { this.setItemShadow(it, true); glowBusy = true; }
        continue;
      }
      if (glowMoving || it.pop > 0) glowBusy = true;
      if (!it.claimed) this.claimGlow(it);

      it.glow += (targetGlow - it.glow) * Math.min(1, dt * 10);
      if (it.pop > 0) it.pop = Math.max(0, it.pop - dt * 2.2);

      // showcase progress 0..1 (eased); reduced motion still floats but never turns.
      // On release a spinning piece first unwinds to a full turn while still up in the air, and only then lowers,
      // so it never sweeps through its neighbours at floor level.
      var rest = it.spinMode === 'full' ? Math.round(it.angle / TWO_PI) * TWO_PI : it.angle;
      var holdUp = !isShown && it.show > 0 && Math.abs(rest - it.angle) > 0.2;
      var lowering = !isShown && !holdUp;
      it.show += ((lowering ? 0 : 1) - it.show) * Math.min(1, dt * (lowering ? 4.5 : 3.2));
      if (isShown) {
        if (!this.reduceMotion && it.spinMode !== 'none') {
          it.spinVel += (0.42 - it.spinVel) * Math.min(1, dt * 1.5);   // eases up to ~1 turn per 15s
          it.angle += it.spinVel * dt;
        }
      } else if (it.show > 0) {
        it.spinVel = 0;
        // calm unwind: proportional, but never faster than ~140 degrees per second
        it.angle += clamp((rest - it.angle) * Math.min(1, dt * 5), -2.4 * dt, 2.4 * dt);
        if (it.show < 0.004 && Math.abs(rest - it.angle) < 0.004) { it.show = 0; it.angle = 0; }
      }
      if (it.show > 0) showcasing = true;

      // the horizontal slide (clear of neighbours) leads, the lift follows; on the way back it lowers first, then slides home
      var slideT = easeInOut(clamp(it.show * 1.8, 0, 1));
      var liftT = easeInOut(clamp((it.show - 0.2) / 0.8, 0, 1));
      var popLift = Math.sin((1 - it.pop) * Math.PI) * 0.06 * (1 - it.show);
      var bob = this.reduceMotion ? 0 : Math.sin(time * 1.7) * 0.018 * liftT;
      it.liftNow = it.glow * 0.035 * (1 - it.show) + popLift + liftT * it.liftHeight + bob;
      it.slideNow.copy(it.slideWorld).multiplyScalar(slideT);
      for (var m = 0; m < it.mats.length; m++) {
        var mat = it.mats[m];
        if (mat.emissive) {
          var be = mat.userData.baseEmissive;
          mat.emissive.setRGB((be ? be.r : 0) + 0.86 * it.glow, (be ? be.g : 0) + 0.6 * it.glow, (be ? be.b : 0) + 0.28 * it.glow);
        }
      }
      var turn = it.spinMode === 'full' ? it.angle : (it.spinMode === 'sway' ? Math.sin(it.angle * 2.2) * (it.swayAmp || 0.16) * liftT : 0);
      it.pivot.position.copy(it.basePos).add(lift.copy(it.localUp).multiplyScalar(it.liftNow)).addScaledVector(it.slideLocal, slideT);
      it.pivot.quaternion.setFromAxisAngle(it.upAxis, turn);
      it.pivot.updateMatrix();            // nothing in the model updates its matrices by itself (see onModel)
      it.pivot.updateMatrixWorld(true);
      if (this.phoneClass || this.liteActive) {
        // Phones: re-drawing the whole shadow map while a piece turns doubled the cost of every third frame, exactly
        // when a product is tapped. Its shadow is simply taken out while it floats (the glow ring grounds it) and put
        // back when it has landed: two shadow passes per showcase instead of about a hundred.
        if (it.show > 0 && !it.shadowOff) this.setItemShadow(it, false);
      } else if (Math.abs(it.liftNow - (it.prevLift || 0)) > 1e-4 || Math.abs(turn - (it.prevTurn || 0)) > 1e-4 || Math.abs(slideT - (it.prevSlide || 0)) > 1e-4) {
        // desktop: the shadow follows the piece, but only when the pose really changed
        this.shadowPending = true;
      }
      it.prevLift = it.liftNow; it.prevTurn = turn; it.prevSlide = slideT;
    }
    this.updateShowcaseRing(time);
    if (this.shadowPending) { this.renderer.shadowMap.needsUpdate = true; this.shadowPending = false; }
    if (showcasing) this.wasShowcasing = true;
    else if (this.wasShowcasing) { this.wasShowcasing = false; this.renderer.shadowMap.needsUpdate = true; glowBusy = true; }
    this.isShowcasing = showcasing;

    // the field of view eases to its target (entering / leaving walk mode, turning the phone)
    var fovMoving = Math.abs(cam.fov - this.fovTarget) > 0.02;
    if (fovMoving) { cam.fov += (this.fovTarget - cam.fov) * Math.min(1, dt * 5); cam.updateProjectionMatrix(); }

    /*
     * Render only what the eye can tell apart. The simulation above runs every frame, the GPU does not have to:
     *  - something is happening (input, a transition, a floating piece, a glow fading): every frame;
     *  - only the slow idle sway of the preview camera: every other frame (it moves ~0.1px per frame, 30fps looks identical);
     *  - nothing moves at all (standing still in walk mode, reduced motion): no rendering until something changes.
     * On a phone this is the difference between a GPU that runs flat out and heats up, and one that mostly rests.
     */
    var k2 = this.keys, anyKey = k2.up || k2.down || k2.left || k2.right || k2.w || k2.a || k2.s || k2.d;
    var calibrating = this.calibFrames > 0 && now - (this.lastScrollAt || 0) > 400;   // never while the page is being scrolled
    var tpBusy = this.mode === 'walk' && !this.transition && !!this.tpBusy, tpIdle = this.mode === 'walk' && !this.transition && !!this.tpIdle;
    var active = this.dirty || !!this.transition || !!this.drag || !!anyKey || showcasing || glowBusy || fovMoving || calibrating || tpBusy;
    var camMoved = !this.lastCamPos || cam.position.distanceToSquared(this.lastCamPos) > 1e-9 || Math.abs(cam.quaternion.dot(this.lastCamQuat)) < 1 - 1e-9;
    var since = now - (this.lastRenderAt || 0);
    var doRender = active || ((camMoved || tpIdle) && since >= 31) || since > 1000;   // tpIdle: the avatar breathing while he stands
    // a phone that is being scrolled gets all of its GPU for the page: the idle preview just keeps its last picture
    // (there is no scroll parallax on phones, so nothing jumps afterwards)
    if (!active && this.phoneClass && this.mode === 'preview' && now - (this.lastScrollAt || 0) < 150) doRender = false;

    this.dirty = false;
    if (doRender) {
      if (this.calibFrames > 0) this.calibFrames--;
      (this.lastCamPos || (this.lastCamPos = new T.Vector3())).copy(cam.position);
      (this.lastCamQuat || (this.lastCamQuat = new T.Quaternion())).copy(cam.quaternion);
      var c0 = performance.now();
      cam.updateMatrixWorld(true);   // dots, card and price are projected with THIS frame's camera, not the previous one
      this.updateOverlays(now);
      this.renderer.render(this.scene, cam);
      // how long JavaScript itself needs for a frame (a running mean): tells a CPU-bound phone from a GPU-bound one
      this.cpuMean = (this.cpuMean || 0) * 0.92 + (performance.now() - c0) * 0.08;
      this.lastRenderAt = now;
    }
    // only back-to-back rendered frames say something about the frame rate
    this.trackPerformance(now, doRender && active && this.renderedLast);
    this.renderedLast = doRender && active;
    this.drewLastTick = doRender;
    if (this.debugEl) this.updateDebug(now);
    // Phones: when nothing at all is happening (no input, no animation, the idle sway has come to rest) the loop stops
    // instead of waking the CPU 60 times a second for as long as the room is on screen. Every input restarts it.
    if (this.phoneClass && !active && !camMoved && !tpIdle && this.swayGain === 0 && !this.debugEl) { this.asleep = true; return; }
    this.asleep = false;
    this.queueFrame();
  };

  /*
   * Clearance plan for a piece, worked out once on its first click:
   *  - very large built-ins and flat wall pieces (mirror, doors) only rise a little, they cannot turn without cutting into a wall;
   *  - small pieces first slide clear of their neighbours (a chair pulls out from under the table) so their turning circle is free;
   *  - large pieces that are hemmed in (table among chairs, island beside stools) sway gently instead of spinning;
   *  - a piece whose turning circle reaches a wall sways, and is nudged away from that wall by the amount the sway needs.
   */
  OakRoom.prototype.ensureClearance = function (it) {
    if (it.cleared) return;
    it.cleared = true;
    var T = this.T, el = this;
    var longSide = Math.max(it.size.x, it.size.z), shortSide = Math.min(it.size.x, it.size.z);
    if (longSide > 3) { it.spinMode = 'none'; it.liftHeight = 0.14; return; }
    if (shortSide < 0.3 && it.size.y > 0.8) { it.spinMode = 'none'; it.liftHeight = 0.2; return; }

    var R = 0.5 * Math.hypot(it.size.x, it.size.z);           // radius of the turning circle
    // compact pieces (chairs, stools, ottoman, nightstands) glide clear and spin; long consoles and big pieces do not glide
    var small = R <= 0.7 || (R <= 1.0 && longSide / Math.max(0.01, shortSide) < 1.6);
    var cx = it.center.x, cz = it.center.z;
    var lo = it.box.min.y + it.liftHeight, hi = it.box.max.y + it.liftHeight;
    var boxes = [];
    this.items.forEach(function (o) { if (o !== it) boxes.push(o.box); });
    (this.obstacles || []).forEach(function (o) { boxes.push(o.box); });
    boxes = boxes.filter(function (b) { return b.max.y > lo + 0.02 && b.min.y < hi - 0.02; }); // only what the floating piece overlaps in height

    var distTo = function (b, x, z) { return Math.hypot(x - clamp(x, b.min.x, b.max.x), z - clamp(z, b.min.z, b.max.z)); };
    if (small) {
      var need = R + 0.04;
      for (var iter = 0; iter < 4; iter++) {
        for (var i = 0; i < boxes.length; i++) {
          var b = boxes[i];
          var px = clamp(cx, b.min.x, b.max.x), pz = clamp(cz, b.min.z, b.max.z);
          var dx = cx - px, dz = cz - pz, d = Math.hypot(dx, dz), move;
          if (d >= need) continue;
          if (d < 1e-4) {
            // the centre is inside the neighbour's footprint: leave through the nearest side
            var toL = cx - b.min.x, toR = b.max.x - cx, toB = cz - b.min.z, toF = b.max.z - cz, m = Math.min(toL, toR, toB, toF);
            dx = m === toL ? -1 : (m === toR ? 1 : 0); dz = m === toB ? -1 : (m === toF ? 1 : 0);
            if (dx !== 0) dz = 0;
            move = m + need;
          } else { dx /= d; dz /= d; move = need - d; }
          cx += dx * move; cz += dz * move;
        }
      }
      // never glide further than a short step; if that is not enough the piece sways instead of spinning
      var sx = cx - it.center.x, sz = cz - it.center.z, sl = Math.hypot(sx, sz);
      if (sl > 0.75) { sx *= 0.75 / sl; sz *= 0.75 / sl; cx = it.center.x + sx; cz = it.center.z + sz; }
    }
    var crowded = boxes.some(function (b) { return distTo(b, cx, cz) < R - 0.01; });
    if (crowded) { it.spinMode = 'sway'; it.swayAmp = small ? 0.12 : 0.07; }

    // walls (and the fitted kitchen run): nearest hit inside the turning circle
    if (this.wallMeshes && this.wallMeshes.length) {
      this.wallMeshes.forEach(function (m) { if (!m.geometry.boundsTree) { try { m.geometry.computeBoundsTree(); } catch (e) { /* ignore */ } } });
      var rc = this.raycaster, y = clamp(it.center.y + it.liftHeight, 0.35, 2.6), nearest = null;
      rc.far = R + 0.06;
      for (var a = 0; a < 16; a++) {
        var ang = (a / 16) * Math.PI * 2;
        rc.ray.origin.set(cx, y, cz);
        rc.ray.direction.set(Math.cos(ang), 0, Math.sin(ang));
        var hits = rc.intersectObjects(this.wallMeshes, false);
        if (hits.length && (!nearest || hits[0].distance < nearest.distance)) nearest = { distance: hits[0].distance, x: Math.cos(ang), z: Math.sin(ang) };
      }
      if (nearest) {
        if (it.spinMode === 'full') { it.spinMode = 'sway'; it.swayAmp = small ? 0.14 : 0.08; }
        // room the sway needs at the far corner, minus the gap that already exists along that direction
        var halfAlong = Math.abs(nearest.x) * it.size.x / 2 + Math.abs(nearest.z) * it.size.z / 2;
        var gap = Math.max(0, nearest.distance - halfAlong);
        var push = clamp(0.5 * longSide * Math.sin(it.swayAmp) + 0.03 - gap, 0, 0.3);
        cx -= nearest.x * push; cz -= nearest.z * push;
      }
    }

    it.slideWorld.set(cx - it.center.x, 0, cz - it.center.z);
    var parent = it.pivot.parent;
    if (parent && it.slideWorld.lengthSq() > 1e-8) {
      var a0 = parent.worldToLocal(it.center.clone());
      var a1 = parent.worldToLocal(it.center.clone().add(it.slideWorld));
      it.slideLocal.copy(a1.sub(a0));
    }
  };

  /* Walk mode: the floor under a floating piece stays off-limits, so nobody is standing there when it lands. */
  OakRoom.prototype.underFloating = function (x, z, fromX, fromZ) {
    for (var i = 0; i < this.items.length; i++) {
      var it = this.items[i], b = it.box, m = 0.4;
      if (!(it.show > 0) || x < b.min.x - m || x > b.max.x + m || z < b.min.z - m || z > b.max.z + m) continue;
      // already inside (e.g. the nook of an L-shaped sofa): only steps that lead away from the centre are allowed
      if (Math.hypot(x - it.center.x, z - it.center.z) < Math.hypot(fromX - it.center.x, fromZ - it.center.z)) return true;
    }
    return false;
  };

  /* A soft amber glow on the floor under the showcased piece (one shared quad with a radial-gradient texture). */
  OakRoom.prototype.buildShowcaseRing = function () {
    var T = this.T;
    if (this.showRing || !this.items.length) return;
    var c = document.createElement('canvas'); c.width = c.height = 256;
    var g = c.getContext('2d');
    var grad = g.createRadialGradient(128, 128, 8, 128, 128, 128);
    grad.addColorStop(0, 'rgba(255, 214, 150, 0.95)');
    grad.addColorStop(0.45, 'rgba(232, 160, 80, 0.45)');
    grad.addColorStop(0.72, 'rgba(232, 160, 80, 0.16)');
    grad.addColorStop(1, 'rgba(232, 160, 80, 0)');
    g.fillStyle = grad; g.fillRect(0, 0, 256, 256);
    var tex = new T.CanvasTexture(c);
    tex.colorSpace = T.SRGBColorSpace;
    var mat = new T.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0, depthWrite: false, blending: 2 /* AdditiveBlending */, toneMapped: false });
    var ring = new T.Mesh(new T.PlaneGeometry(1, 1), mat);
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 2;
    ring.visible = false;
    ring.raycast = function () {}; // never pickable, never a collision surface
    this.scene.add(ring);
    this.showRing = ring;
    this.ringItem = null;
  };
  OakRoom.prototype.updateShowcaseRing = function (time) {
    var ring = this.showRing;
    if (!ring) return;
    // hand the glow and the price over to a newly clicked piece only once the previous one is nearly down,
    // so they fade with the old piece instead of popping away while it is still in the air
    if (this.selected && (!this.ringItem || this.ringItem === this.selected || this.ringItem.show < 0.15)) this.ringItem = this.selected;
    var it = this.ringItem;
    if (!it || it.show <= 0.001) { if (ring.visible) { ring.visible = false; ring.material.opacity = 0; } if (it && it.show === 0) this.ringItem = null; return; }
    // a floor glow only makes sense under pieces that stand on the floor (not under a wall mirror)
    if (!it.onFloor) { ring.visible = false; ring.material.opacity = 0; return; }
    var d = Math.min(Math.max(it.size.x, it.size.z) * 1.35 + 0.35, 3.2);
    var pulse = this.reduceMotion ? 1 : 1 + Math.sin(time * 2.1) * 0.04;
    ring.visible = true;
    ring.position.set(it.center.x + it.slideNow.x, it.box.min.y + 0.03, it.center.z + it.slideNow.z);
    ring.scale.set(d * pulse, d * pulse, 1);
    ring.updateMatrixWorld(true);   // the scene no longer updates matrices on its own (see onModel)
    ring.material.opacity = 0.85 * it.show;
  };

  OakRoom.prototype.updateOverlays = function (now) {
    var cam = this.camera, w = this.width, h = this.height, v = this.tmpV;
    // 3D price label: sits just ABOVE the floating piece and scales with distance so it feels part of the room.
    // It is placed first (pr); the info card is then stacked above it so the two never overlap.
    var priceEl = this.ui.price, pit = this.ringItem, pr = null;
    if (priceEl) {
      var showPrice = pit && pit.show > 0.02 && pit.hotspot && pit.hotspot.price;
      if (showPrice) {
        if (this.priceItem !== pit) {
          this.priceItem = pit;
          var pt = priceEl.querySelector('[data-oak-price3d-text]');
          if (pt) { pt.textContent = pit.hotspot.price; pt.setAttribute('data-text', pit.hotspot.price); }
          priceEl.classList.remove('is-visible'); // re-added below: restarts the pop-in for every new price
          // layout size of the label (unaffected by transforms): read once per text change, never per frame
          this.priceW = priceEl.offsetWidth; this.priceH = priceEl.offsetHeight;
        }
        v.set(pit.anchor.x + pit.slideNow.x, pit.anchor.y + pit.liftNow + 0.06, pit.anchor.z + pit.slideNow.z);
        var dist = v.distanceTo(cam.position);
        v.project(cam);
        var px = (v.x * 0.5 + 0.5) * w, py = (-v.y * 0.5 + 0.5) * h;
        if (!(v.z > 1 || px < -60 || px > w + 60 || py < -60 || py > h + 60)) {
          var pw = this.priceW || 78, ph = this.priceH || 28;
          // a narrow range: the price stays small next to the furniture and never balloons when the camera is close
          var s = clamp(4.6 / Math.max(0.6, dist), 0.9, 1.25) * (0.7 + 0.3 * pit.show);
          s = Math.min(s, (w - 16) / Math.max(1, pw));               // a long price never grows wider than the stage
          var halfW = pw * s / 2 + 8;
          pr = { x: clamp(px, halfW, Math.max(halfW, w - halfW)), y: clamp(py, ph * s + 6, Math.max(ph * s + 6, h - 6)), s: s, h: ph * s, o: clamp(pit.show * 1.4 - 0.2, 0, 1) };
        }
      }
      if (!pr && priceEl.classList.contains('is-visible')) { priceEl.classList.remove('is-visible'); if (!showPrice) this.priceItem = null; }
    }
    // the card follows the piece it describes (hovered or clicked), including while it floats and slides
    if (this.cardItem && this.ui.card) {
      var ci = this.cardItem;
      v.set(ci.anchor.x + ci.slideNow.x, ci.anchor.y + (ci.liftNow || 0), ci.anchor.z + ci.slideNow.z);
      v.project(cam);
      var behind = v.z > 1;
      var x = (v.x * 0.5 + 0.5) * w, y = (-v.y * 0.5 + 0.5) * h;
      // a hovered card hides when its anchor leaves the view; a clicked (pinned) card is clamped into the stage instead
      var outside = x < -80 || x > w + 80 || y < -40 || y > h + 40;
      if (behind || (outside && !this.pinned)) this.ui.card.classList.add('is-offscreen');
      else {
        var half = Math.min(this.compact ? 116 : 130, w / 2 - 8);
        // walk mode on a small screen: the card keeps clear of the Exit button / hint at the top and the arrows at the bottom
        var walkUi = this.mode === 'walk' && (this.isTouch || this.compact);
        // Exit button, plus the view switch under it (beside it only on a phone held sideways, as in the CSS)
        var viewUnder = this.ui.viewBtn && !(w >= h && h <= 520);
        var minY = (this.cardHeight || 140) + 14 + (walkUi ? (viewUnder ? 112 : 56) : 0);
        var maxY = h - (walkUi && h > 520 ? 170 : 20);
        var stacked = pr && ci === pit;
        var target = stacked ? pr.y - pr.h - 12 : y - 14;         // above the price when both describe the same piece
        var cy = clamp(target, minY, Math.max(minY, maxY));
        // near the top edge the card cannot go any higher, so the price moves down to stay below it
        if (stacked && cy > target + 0.5) pr.y = Math.min(h - 6, cy + 12 + pr.h);
        this.ui.card.classList.remove('is-offscreen');
        this.ui.card.style.transform = 'translate(' + Math.round(clamp(x, half + 8, w - half - 8)) + 'px,' + Math.round(cy) + 'px) translate(-50%,-100%)';
      }
    }
    if (pr) {
      priceEl.style.transform = 'translate(' + Math.round(pr.x) + 'px,' + Math.round(pr.y) + 'px) translate(-50%, -100%) scale(' + pr.s.toFixed(3) + ')';
      priceEl.style.opacity = String(pr.o);
      priceEl.classList.add('is-visible');
    }
    // markers: project + occlusion test (staggered, every ~250ms per marker).
    // When one product is placed several times (8 dining chairs) only the closest visible copy gets a dot.
    if (this.markerEls.length) {
      var showMarkers = this.mode === 'preview' && !this.transition;
      var doOcclude = this.bvhReady;
      var best = {};
      for (var i = 0; i < this.markerEls.length; i++) {
        var mk = this.markerEls[i];
        mk.show = false;
        if (!showMarkers) continue;
        // a floating piece hides its dot, unless the dot has keyboard focus: then it rides along so focus is never lost
        var floating = mk.item.show > 0.05, focused = floating && document.activeElement === mk.el;
        if (floating && !focused) continue;
        v.set(mk.item.anchor.x + mk.item.slideNow.x, mk.item.anchor.y + (mk.item.liftNow || 0), mk.item.anchor.z + mk.item.slideNow.z).project(cam);
        var inFront = v.z < 1 && v.x > -0.98 && v.x < 0.98 && v.y > -0.98 && v.y < 0.98;
        if (!inFront) continue;
        mk.sx = (v.x * 0.5 + 0.5) * w; mk.sy = (-v.y * 0.5 + 0.5) * h;
        if (focused) { mk.show = true; continue; } // no occlusion test or de-duplication for the focused marker
        var dirv = this.tmpV2.copy(mk.item.anchor).sub(cam.position);
        var dist = dirv.length();
        if (doOcclude && now - mk.lastCheck > 250) {
          mk.lastCheck = now + i * 7;
          this.raycaster.ray.origin.copy(cam.position);
          this.raycaster.ray.direction.copy(dirv.divideScalar(dist));
          this.raycaster.far = dist - 0.05;
          var hits = this.raycaster.intersectObjects(this.allMeshes, false);
          mk.occluded = hits.length > 0 && hits[0].object.userData.oakItem !== mk.item && hits[0].distance < dist - 0.25;
        }
        if (mk.occluded) continue;
        var key = mk.item.hotspot ? (mk.item.hotspot.match || mk.item.label) : mk.item.name;
        if (!best[key] || best[key].dist > dist) best[key] = { mk: mk, dist: dist };
      }
      for (var k in best) best[k].mk.show = true;
      for (var j = 0; j < this.markerEls.length; j++) {
        var m2 = this.markerEls[j];
        if (m2.show) m2.el.style.transform = 'translate(' + Math.round(m2.sx) + 'px,' + Math.round(m2.sy) + 'px)';
        if (m2.show !== m2.visible) { m2.visible = m2.show; m2.el.classList.toggle('is-visible', m2.show); }
      }
    }
  };

  /* ==========================================================================================================
   * Third-person view: a walking avatar and a follow camera
   * ----------------------------------------------------------------------------------------------------------
   * The avatar file is a rigged body WITHOUT animation clips, so its motion is made here, procedurally: a walk
   * cycle (legs, knees, feet, toes, pelvis, spine, arms, head) driven by the real distance covered, which keeps
   * the feet from sliding at any speed, a jog when Shift is held, an idle stance with breathing, and leaning into
   * turns. About twenty bones are posed per frame with reused maths objects: no animation mixer, no key-frame
   * data to download, nothing allocated while walking.
   * ========================================================================================================== */
  var RIG_BONES = ['Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head',
    'LeftShoulder', 'LeftArm', 'LeftForeArm', 'LeftHand', 'RightShoulder', 'RightArm', 'RightForeArm', 'RightHand',
    'LeftUpLeg', 'LeftLeg', 'LeftFoot', 'LeftToeBase', 'RightUpLeg', 'RightLeg', 'RightFoot', 'RightToeBase'];

  function PersonRig(T, model) {
    var rig = this;
    this.T = T;
    this.root = new T.Group();
    this.root.name = 'oak-person';
    this.root.add(model);
    this.meshes = [];
    this.bones = {};
    this.phase = 0; this.speed = 0; this.move = 0; this.gait = 0; this.lean = 0; this.idleGain = 1;
    this.q = new T.Quaternion(); this.q2 = new T.Quaternion(); this.e = new T.Euler(0, 0, 0, 'YXZ');
    var found = {};
    model.traverse(function (o) {
      if (o.isBone) { var key = o.name.replace(/_\d+$/, ''); if (!found[key]) found[key] = o; }
      if (o.isSkinnedMesh) {
        o.frustumCulled = false;      // the bounding sphere is that of the rest pose at the origin
        o.castShadow = false;         // the room's shadows are baked: the avatar gets a soft blob under its feet instead
        o.receiveShadow = true;
        rig.meshes.push(o);
      }
    });
    this.root.updateMatrixWorld(true);
    // Every posed bone remembers its rest rotation and its parent's rest orientation in avatar space. A pose is then
    // written as a rotation about the AVATAR's axes (x = left/right, y = up, z = forward), whatever the bone's own axes are:
    //   local = parentRest^-1 * D * parentRest * restLocal
    RIG_BONES.forEach(function (key) {
      var b = found[key];
      if (!b) return;
      var pw = new T.Quaternion();
      b.parent.getWorldQuaternion(pw);
      rig.bones[key] = { bone: b, rest: b.quaternion.clone(), pw: pw, pwInv: pw.clone().invert(), restPos: b.position.clone() };
    });
    this.ok = !!(this.bones.Hips && this.bones.LeftUpLeg && this.bones.RightUpLeg && this.bones.LeftLeg && this.bones.RightLeg);
    // arms: how far they have to come down from the rest pose (T or A pose) to hang beside the body, and the hinge the
    // elbow bends about; both are measured from the file, so another avatar with the same bone names works too
    ['Left', 'Right'].forEach(function (side) {
      var arm = found[side + 'Arm'], fore = found[side + 'ForeArm'], hand = found[side + 'Hand'];
      if (!arm || !fore || !hand || !rig.bones[side + 'Arm']) return;
      var a = new T.Vector3(), f = new T.Vector3(), h = new T.Vector3();
      arm.getWorldPosition(a); fore.getWorldPosition(f); hand.getWorldPosition(h);
      var dir = f.clone().sub(a).normalize();
      var sx = side === 'Left' ? 1 : -1;
      var down = new T.Vector3(sx * 0.13, -1, 0.02).normalize();          // hanging, a little away from the hip
      rig.bones[side + 'Arm'].down = new T.Quaternion().setFromUnitVectors(dir, down);
      var foreDir = h.clone().sub(f).normalize();
      var hinge = new T.Vector3().crossVectors(foreDir, new T.Vector3(0, 0, 1));
      if (hinge.lengthSq() < 1e-4) hinge.set(0, -sx, 0);
      rig.bones[side + 'ForeArm'].hinge = hinge.normalize();
    });
    ['LeftFoot', 'RightFoot', 'LeftToeBase', 'RightToeBase'].forEach(function (k) { if (rig.bones[k]) rig.bones[k].restY = rig.bones[k].bone.getWorldPosition(new T.Vector3()).y; });
    var hips = found.Hips, foot = found.LeftFoot, p1 = new T.Vector3(), p2 = new T.Vector3();
    this.legLength = 0.9;
    if (hips && foot) { found.LeftUpLeg.getWorldPosition(p1); foot.getWorldPosition(p2); this.legLength = Math.max(0.5, p1.y - p2.y); }
  }
  /* pose one bone: rotation D (avatar axes, relative to its parent) on top of the rest pose */
  PersonRig.prototype.set = function (key, D) {
    var r = this.bones[key];
    if (!r) return;
    r.bone.quaternion.copy(r.pwInv).multiply(D).multiply(r.pw).multiply(r.rest);
  };
  PersonRig.prototype.setEuler = function (key, x, y, z) {
    this.e.set(x, y, z, 'YXZ');
    this.set(key, this.q.setFromEuler(this.e));
  };
  function bump(p, centre, power) {          // a smooth hill around "centre" (radians), zero on the far half of the cycle
    var c = Math.cos(p - centre);
    return c > 0 ? Math.pow(c, power) : 0;
  }
  /*
   * dt seconds passed, the body covered `dist` metres along its facing; `turn` is its turning speed (rad/s).
   * Phase 0..2pi is one full stride of the LEFT leg (the right leg is half a cycle behind).
   */
  PersonRig.prototype.update = function (dt, time, dist, turn, still) {
    if (!this.ok) return;
    var speed = dt > 0 ? dist / dt : 0;
    this.speed += (speed - this.speed) * Math.min(1, dt * 10);
    var moving = this.speed > 0.12 ? 1 : 0;
    this.move += (moving - this.move) * Math.min(1, dt * (moving ? 9 : 6));
    var gaitTarget = clamp((this.speed - 2.2) / 0.9, 0, 1);              // 0 = walk, 1 = jog (Shift)
    this.gait += (gaitTarget - this.gait) * Math.min(1, dt * 5);
    this.lean += (clamp(turn * 0.05, -0.1, 0.1) - this.lean) * Math.min(1, dt * 6);
    this.idleGain += ((still ? 0 : 1) - this.idleGain) * Math.min(1, dt * 1.5);
    var m = this.move, g = this.gait, idle = (1 - m) * this.idleGain;
    // stride length follows the leg swing, so the planted foot stays where it is on the floor
    var A = (0.4 + 0.2 * g) * (0.55 + 0.45 * clamp(this.speed / 1.9, 0, 1.2));   // hip swing amplitude (radians)
    var stride = 4 * this.legLength * Math.sin(A) * (1 + 0.35 * g);             // metres per full cycle (the jog has a flight phase)
    this.phase = (this.phase + (dist / Math.max(0.4, stride)) * Math.PI * 2) % (Math.PI * 2);
    var p = this.phase, PI = Math.PI;

    // ---- pelvis
    var hipsR = this.bones.Hips;
    // highest over the standing leg, lowest with both feet down; the jog rises in its flight phase (values fitted so the
    // soles stay on the floor: the first version dipped the shoes 4-10cm into it)
    var bob = (0.0062 * Math.cos(2 * p) - 0.0031 * Math.sin(2 * p) - 0.0186) * (1 - g) + (0.02 * Math.sin(p) * Math.sin(p) - 0.004) * g;
    var sway = -0.022 * Math.cos(p) * (1 - 0.5 * g);                             // over the standing leg
    var ib = Math.sin(time * 1.35) * 0.004, isw = Math.sin(time * 0.45) * 0.012;
    hipsR.bone.position.set(hipsR.restPos.x + sway * m + isw * idle, hipsR.restPos.y + bob * m + ib * idle, hipsR.restPos.z);
    var pelYaw = -0.1 * Math.sin(p) * m, pelRoll = -0.045 * Math.cos(p) * m + isw * 1.2 * idle;
    this.setEuler('Hips', (0.03 + 0.07 * g) * m, pelYaw, pelRoll - this.lean * 0.6);

    // ---- spine: shoulders turn against the pelvis, a little forward lean, breathing when idle
    var breath = Math.sin(time * 1.35) * 0.012 * idle;
    var spYaw = (0.14 + 0.05 * g) * Math.sin(p) * m, spLean = (0.035 + 0.1 * g) * m;
    this.setEuler('Spine', spLean * 0.4 + breath, spYaw * 0.3, -pelRoll * 0.5 - this.lean * 0.5);
    this.setEuler('Spine1', spLean * 0.3 + breath * 0.6, spYaw * 0.35, -pelRoll * 0.3);
    this.setEuler('Spine2', spLean * 0.3 - breath * 0.4, spYaw * 0.35, 0);
    var look = Math.sin(time * 0.31) * 0.09 * idle;
    this.setEuler('Neck', -spLean * 0.4, -spYaw * 0.35 + look * 0.4, 0);
    this.setEuler('Head', -spLean * 0.45 - 0.02 * m, -(spYaw + pelYaw) * 0.35 + look * 0.6 + this.lean * 1.2, this.lean * 0.4);

    // ---- legs
    for (var s = 0; s < 2; s++) {
      var side = s === 0 ? 'Left' : 'Right', lp = s === 0 ? p : p + PI;
      var swing = Math.sin(lp);                                                   // +1 = leg fully forward
      var hip = (-A * swing - 0.06 * g) * m;                                      // negative x = forward
      var lift = bump(lp, PI * 2 - 0.3, 1.9);                                     // knee folds as the leg passes under the body (not while it still trails)
      var load = bump(lp, PI / 2 + 0.55, 3);                                      // and gives a little when it takes the weight
      var knee = (0.05 + (0.86 + 0.75 * g) * lift + 0.17 * load) * m + 0.035 * (1 - m);
      var heel = bump(lp, PI / 2 - 0.1, 5), toeOff = bump(lp, PI * 3 / 2 + 0.25, 3.2);
      var footWorld = (-0.3 * heel + (0.62 + 0.2 * g) * toeOff - 0.12 * lift) * m;  // toes up at heel strike, heel up at push-off
      this.setEuler(side + 'UpLeg', hip + 0.02 * (1 - m), 0, (s === 0 ? -1 : 1) * (0.02 + 0.015 * m) + pelRoll * 0.6);
      this.setEuler(side + 'Leg', knee, 0, 0);
      this.setEuler(side + 'Foot', footWorld - hip - knee, 0, 0);                 // cancels the leg, so the sole lies flat while it stands
      this.setEuler(side + 'ToeBase', -0.55 * toeOff * m, 0, 0);                  // toes stay on the floor while the heel rises
    }

    // floor guard: no ankle or toe joint ever goes below its rest height, at any speed and while starting or stopping
    // (heights do not depend on the root's x/z/yaw, so last frame's parent matrix is fine)
    hipsR.bone.updateWorldMatrix(false, false);
    var sink = 0;
    for (var f = 0; f < 2; f++) {
      this.bones[f ? 'RightUpLeg' : 'LeftUpLeg'].bone.updateWorldMatrix(false, true);
      for (var j = 0; j < 2; j++) {
        var fr = this.bones[(f ? 'Right' : 'Left') + (j ? 'ToeBase' : 'Foot')];
        if (fr && fr.restY !== undefined) sink = Math.max(sink, fr.restY + this.root.position.y - fr.bone.matrixWorld.elements[13]);
      }
    }
    hipsR.bone.position.y += sink;

    // ---- arms swing against the legs; elbows bend more on the way forward, and stay bent in a jog
    for (var a = 0; a < 2; a++) {
      var sd = a === 0 ? 'Left' : 'Right', ap = a === 0 ? p : p + PI, sx = a === 0 ? 1 : -1;
      var armR = this.bones[sd + 'Arm'], foreR = this.bones[sd + 'ForeArm'];
      if (!armR || !armR.down) continue;
      var sw = Math.sin(ap);                                                       // +1 = same-side leg forward -> this arm is back
      var armSwing = ((0.33 + 0.22 * g) * sw + 0.03) * m + (0.03 + Math.sin(time * 0.9 + a) * 0.015) * idle;
      this.e.set(armSwing, 0, sx * (-0.02 - 0.05 * g * m), 'YXZ');
      this.q2.setFromEuler(this.e).multiply(armR.down);                            // first down beside the body, then the swing
      this.set(sd + 'Arm', this.q2);
      if (foreR && foreR.hinge) {
        // (the rest pose already has the elbow a little bent, so the walk only adds a touch on the forward swing)
        var bend = (0.02 + 0.2 * Math.max(0, -sw)) * m * (1 - g) + (1.05 + 0.15 * -sw) * m * g + 0.06 * (1 - m);
        this.set(sd + 'ForeArm', this.q.setFromAxisAngle(foreR.hinge, bend));
      }
      this.setEuler(sd + 'Hand', 0, 0, sx * -0.08);
      this.setEuler(sd + 'Shoulder', 0, 0, sx * 0.03 * m);
    }
  };

  /* ---------- loading the avatar (lazily: nothing is downloaded until walk mode is entered) ---------- */
  OakRoom.prototype.loadPerson = function () {
    var el = this, T = this.T, url = this.dataset.person;
    if (this.personPromise) return this.personPromise;
    if (!url || !T) return Promise.reject(new Error('no avatar'));
    this.personPromise = new Promise(function (resolve, reject) {
      var loader = new T.GLTFLoader();
      loader.setMeshoptDecoder(T.MeshoptDecoder);
      loader.load(url, function (gltf) {
        if (el.disposed || !el.renderer) { reject(new Error('gone')); return; }
        var rig = new PersonRig(T, gltf.scene);
        if (!rig.ok) { reject(new Error('avatar has no usable skeleton')); return; }
        rig.meshes.forEach(function (m) {
          var mat = m.material;
          m.userData.matFull = mat;
          if (mat.map) mat.map.anisotropy = 1;
          mat.envMapIntensity = 1;
          m.receiveShadow = el.shadowsLive !== false;
          if (el.liteActive) m.material = el.liteOf(mat);
        });
        rig.root.visible = false;
        // a soft contact shadow under the feet: one textured quad instead of re-rendering the whole shadow map every frame
        var c = document.createElement('canvas'); c.width = c.height = 64;
        var g2 = c.getContext('2d'), grad = g2.createRadialGradient(32, 32, 2, 32, 32, 31);
        grad.addColorStop(0, 'rgba(20,14,8,0.55)'); grad.addColorStop(0.55, 'rgba(20,14,8,0.22)'); grad.addColorStop(1, 'rgba(20,14,8,0)');
        g2.fillStyle = grad; g2.fillRect(0, 0, 64, 64);
        var tex = new T.CanvasTexture(c); tex.colorSpace = T.SRGBColorSpace;
        var blob = new T.Mesh(new T.PlaneGeometry(1, 1), new T.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, toneMapped: false }));
        blob.rotation.x = -Math.PI / 2; blob.renderOrder = 3; blob.visible = false;
        rig.blob = blob;
        el.scene.add(rig.root); el.scene.add(blob);
        rig.root.updateMatrixWorld(true);
        el.person = rig;
        // compile the (skinned) shaders in the background before the avatar is ever shown
        var ready = function () { rig.ready = true; resolve(rig); };
        try { el.renderer.compileAsync(rig.root, el.camera, el.scene).then(ready, ready); } catch (e) { ready(); }
      }, undefined, function (err) { el.personPromise = null; reject(err); });
    });
    return this.personPromise;
  };

  /* ---------- switching between first and third person ---------- */
  OakRoom.prototype.setView = function (view) {
    var el = this;
    if (this.mode !== 'walk' || !this.dataset.person) view = 'first';
    if (view === this.view) return;
    if (view === 'third') {
      this.setViewBusy(true);
      this.loadPerson().then(function (rig) {
        el.setViewBusy(false);
        if (el.mode !== 'walk' || el.disposed) return;
        el.view = 'third';
        // he stands where the visitor stood, facing the way the visitor was looking
        rig.yaw = el.walk.yaw + Math.PI;
        rig.phase = 0; rig.speed = 0; rig.move = 0;
        el.tpDist = 0.4;
        el.tpSqueeze = el.tpSqueezeTo = undefined; el.tpAllowed = undefined;   // measured afresh at this spot
        el.classList.add('is-third');
        el.updateViewButton();
        el.requestFrame();
      }).catch(function () { el.setViewBusy(false); el.view = 'first'; el.updateViewButton(); });
    } else {
      this.view = 'first';
      this.classList.remove('is-third');
      this.updateViewButton();
      this.requestFrame();
    }
  };
  OakRoom.prototype.toggleView = function () { this.setView(this.view === 'third' ? 'first' : 'third'); };
  OakRoom.prototype.setViewBusy = function (busy) {
    if (this.ui.viewBtn) this.ui.viewBtn.classList.toggle('is-busy', !!busy);
  };
  OakRoom.prototype.updateViewButton = function () {
    var b = this.ui.viewBtn;
    if (!b) return;
    var third = this.view === 'third';
    b.setAttribute('aria-pressed', third ? 'true' : 'false');
    // the name stays "Third person"; aria-pressed (and the gold style) carries the state. Changing both would tell a
    // screen reader the opposite of what is on screen.
  };

  /*
   * The walk-mode camera. First person: at the visitor's eyes. Third person: on a boom behind the avatar that a wall or a
   * piece of furniture pushes in (one ray per frame against the collision meshes), so the camera never ends up inside
   * something. The boom follows the avatar with NO lag (what the thumb does is on screen in the same frame); only its
   * length is smoothed. `blend` 0..1 slides between the two views when the button is pressed.
   */
  OakRoom.prototype.updateWalkCamera = function (dt, time, moved) {
    var T = this.T, cam = this.camera, w = this.walk, rig = this.person;
    var third = this.view === 'third' && rig && rig.ready;
    var target = third ? 1 : 0;
    this.tpBlend = this.tpBlend || 0;
    if (this.tpBlend !== target) {
      this.tpBlend += (target - this.tpBlend) * Math.min(1, dt * (this.reduceMotion ? 30 : 5.5));
      if (Math.abs(target - this.tpBlend) < 0.004) this.tpBlend = target;
    }
    var blend = this.tpBlend;
    // first-person pose
    var fpY = w.pos.y + Math.sin(w.bob) * 0.012 * (1 - blend);
    this.tmpE.set(w.pitch, w.yaw, 0, 'YXZ');
    if (!rig || blend === 0) {
      cam.position.set(w.pos.x, fpY, w.pos.z);
      cam.quaternion.setFromEuler(this.tmpE);
      if (rig && rig.root.visible) { rig.root.visible = false; rig.blob.visible = false; }
      this.tpBusy = false; this.tpIdle = false; this.tpLastX = this.tpLastZ = undefined;
      return;
    }
    // ---- the avatar: stands on the floor under the visitor's position, turns to face where he walks
    var dx = w.pos.x - (this.tpLastX === undefined ? w.pos.x : this.tpLastX), dz = w.pos.z - (this.tpLastZ === undefined ? w.pos.z : this.tpLastZ);
    this.tpLastX = w.pos.x; this.tpLastZ = w.pos.z;
    var dist = Math.hypot(dx, dz), turn = 0;
    if (dist > 0.0005 && dt > 0) {
      var want = Math.atan2(dx, dz), d = want - rig.yaw;
      d = Math.atan2(Math.sin(d), Math.cos(d));
      var stepYaw = clamp(d, -9 * dt, 9 * dt);
      rig.yaw += stepYaw; turn = stepYaw / dt;
    }
    rig.root.position.set(w.pos.x, 0.011, w.pos.z);   // the floor surface of the room model is at y = 0.011, not 0
    rig.root.rotation.y = rig.yaw;
    var still = this.phoneClass && performance.now() - (this.lastInputAt || 0) > 20000;   // an unattended phone: he stands still, the loop may sleep
    rig.update(dt, time, dist, turn, still || this.reduceMotion);   // reduced motion: he still walks, but does not fidget while standing
    rig.root.visible = blend > 0.3;
    rig.blob.visible = rig.root.visible;
    rig.blob.position.set(w.pos.x, 0.045, w.pos.z);
    var bs = 0.95 - 0.12 * rig.move;
    rig.blob.scale.set(bs, bs, 1);
    rig.blob.material.opacity = clamp((blend - 0.3) / 0.4, 0, 1);
    rig.root.updateMatrixWorld(true);   // the scene does not update matrices on its own (see onModel)
    rig.blob.updateMatrixWorld(true);

    // ---- the boom
    var tall = cam.aspect < 0.85;
    var want3 = tall ? 3.3 : 2.75;
    // Looking down a little by default. When something behind squeezes the boom short, the camera rises and looks over
    // his head instead of staring at the back of his hoodie. The squeeze is measured by its OWN ray along the un-raised
    // boom: if it depended on the smoothed boom length, raising the camera would lengthen the boom, which would lower
    // the camera again, and the boom would pump in and out for ever. Rays are only cast when the pose changed.
    var rc = this.raycaster, yaw = w.yaw, blockers = this.boomBlockers && this.boomBlockers.length ? this.boomBlockers : this.collidables;
    var head = this.tmpV.set(w.pos.x, 1.42, w.pos.z), dir = this.tmpV2;
    var basePitch = clamp(-w.pitch + 0.2, -0.25, 1.05);
    if (moved || yaw !== this.tpYaw || basePitch !== this.tpBase || want3 !== this.tpWant || this.tpSqueezeTo === undefined) {
      this.tpYaw = yaw; this.tpBase = basePitch; this.tpWant = want3; this.tpPitch = undefined;
      rc.ray.origin.copy(head); rc.ray.direction.set(Math.sin(yaw) * Math.cos(basePitch), Math.sin(basePitch), Math.cos(yaw) * Math.cos(basePitch)); rc.near = 0; rc.far = want3 + 0.3;
      var h0 = rc.intersectObjects(blockers, false);
      this.tpSqueezeTo = h0.length ? clamp(1 - Math.max(0.45, h0[0].distance - 0.25) / want3, 0, 1) : 0;
      if (this.tpSqueeze === undefined) this.tpSqueeze = this.tpSqueezeTo;
    }
    this.tpSqueeze += (this.tpSqueezeTo - this.tpSqueeze) * Math.min(1, dt * (this.tpSqueezeTo > this.tpSqueeze ? 20 : 3.5));
    if (Math.abs(this.tpSqueezeTo - this.tpSqueeze) < 0.002) this.tpSqueeze = this.tpSqueezeTo;
    var pitch = clamp(basePitch + 0.5 * this.tpSqueeze, -0.25, 1.05);
    dir.set(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch));
    var allowed = want3;
    if (pitch !== this.tpPitch || this.tpAllowed === undefined) {
      this.tpPitch = pitch;
      rc.ray.origin.copy(head); rc.ray.direction.copy(dir); rc.near = 0; rc.far = want3 + 0.3;
      var hits = rc.intersectObjects(blockers, false);
      if (hits.length) allowed = Math.max(0.45, hits[0].distance - 0.25);
      this.tpAllowed = allowed;
    } else allowed = this.tpAllowed;
    this.tpDist = this.tpDist || allowed;
    this.tpDist += (allowed - this.tpDist) * Math.min(1, dt * (allowed < this.tpDist ? 20 : 3.5));   // in fast, out gently
    var tp = this.tmpV3.copy(head).addScaledVector(dir, this.tpDist);
    tp.y = clamp(tp.y, 0.35, (this.bounds ? this.bounds.max.y : 3.4) - 0.25);
    // look a little above the head, so the body sits in the lower half of the picture and the room fills the rest
    var lookAt = (this.tpLook || (this.tpLook = new T.Vector3())).set(w.pos.x, 1.42 + (tall ? 0.02 : 0.22), w.pos.z);   // upright phones: lower, or half the picture is ceiling
    var mtx = (this.tmpM || (this.tmpM = new T.Matrix4())).lookAt(tp, lookAt, this.upV || (this.upV = new T.Vector3(0, 1, 0)));
    var tq = this.tmpQ.setFromRotationMatrix(mtx);
    if (blend === 1) { cam.position.copy(tp); cam.quaternion.copy(tq); }
    else {
      var eb = easeInOut(blend);
      cam.quaternion.setFromEuler(this.tmpE);
      cam.position.set(w.pos.x, fpY, w.pos.z).lerp(tp, eb);
      cam.quaternion.slerp(tq, eb);
    }
    // so close to a wall that the camera sits on his shoulder: hide the body rather than look through it
    // (also during the glide between the views: never a close-up of the inside of his head)
    if (cam.position.distanceToSquared(head) < 0.49) { rig.root.visible = false; rig.blob.visible = false; }
    // busy = something is still settling (the view switch, the boom, his last step): draw every frame.
    // idle = he only breathes: the odd frame (30 a second) is enough, and an unattended phone may stop altogether.
    this.tpBusy = blend !== target || rig.move > 0.01 || Math.abs(allowed - this.tpDist) > 0.01 || this.tpSqueeze !== this.tpSqueezeTo;
    this.tpIdle = rig.root.visible && !still && !this.reduceMotion;
  };

  /* ---------- quality ladder ----------
   * Sharp textures come first: resolution is what makes a texture crisp, the kind of shading is what nobody notices.
   * A device that cannot hold the frame rate therefore gives things up in this order:
   *   environment-lit shading (matte surfaces switch to a much cheaper material that uses the SAME textures)
   *   -> resolution above 1.5x -> the sun's shadows -> resolution, and never below one canvas pixel per CSS pixel.
   * Entry-level phone GPUs, recognised by name, start on the cheap shading straight away (and without an environment
   * map at all), so they never even compile the expensive shaders.
   */
  OakRoom.prototype.initQuality = function () {
    var dpr = window.devicePixelRatio || 1;
    //            ratio, lite, shadows
    var steps = this.phoneClass
      ? [[2, 0, 1], [2, 1, 1], [1.75, 1, 1], [1.5, 1, 1], [1.5, 1, 0], [1.25, 1, 0], [1, 1, 0]]
      : [[1.75, 0, 1], [1.5, 0, 1], [1.5, 1, 1], [1.25, 1, 1], [1, 1, 1], [1, 1, 0]];
    var ladder = [], last = '';
    steps.forEach(function (s) {
      var q = { ratio: Math.min(dpr, s[0]), lite: !!s[1], shadows: !!s[2] };
      var key = q.ratio + '|' + q.lite + '|' + q.shadows;
      if (key !== last) ladder.push(q);   // steps that change nothing on this screen are dropped
      last = key;
    });
    this.ladder = ladder;
    var start = 0, i, weak = this.weakGpu() || this.dataset.weak === '1';
    this.firstLite = 0;
    for (i = 0; i < ladder.length; i++) if (ladder[i].lite) { this.firstLite = i; break; }
    this.weak = weak;
    if (weak) { for (i = 0; i < ladder.length; i++) if (ladder[i].lite && ladder[i].ratio <= 1.5) { start = i; break; } }
    // What this device settled on last time is kept for one day, and the next visit starts ONE step above it: a phone
    // that was only hot or busy that day works its way back up, a genuinely slow one steps down once and stays there.
    try {
      var saved = JSON.parse(window.localStorage.getItem('oak3d:quality') || 'null');
      if (saved && saved.n === ladder.length && Date.now() - saved.t < 864e5) start = Math.max(start, Math.min(ladder.length - 1, (saved.i | 0) - 1));
    } catch (e) { /* private mode */ }
    // data-quality="0..6" pins a step, data-weak="1" forces the weak-GPU path (testing / support); the governor is then off
    var forced = parseInt(this.dataset.quality, 10);
    if (!isNaN(forced)) { start = clamp(forced, 0, ladder.length - 1); this.qualityLocked = true; }
    // weak GPUs drop the environment map for good: decided once, before anything is compiled
    this.noEnv = weak && ladder[start].lite;
    this.qIndex = start;
    this.quality = ladder[start];
    this.pixelRatio = this.quality.ratio;
    this.liteCache = {};
  };
  /* Entry-level phone GPUs, old integrated graphics and software renderers, recognised by name. */
  OakRoom.prototype.weakGpu = function () {
    var name = '', small = false;
    try {
      var gl = this.renderer.getContext();
      var ext = gl.getExtension('WEBGL_debug_renderer_info');
      name = String((ext && gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || '');
      small = gl.getParameter(gl.MAX_TEXTURE_SIZE) <= 4096;
    } catch (e) { /* ignore */ }
    this.gpuName = name;
    if (/Apple/i.test(name)) return false;
    return small || !!this.modestDevice ||
      /Mali-(4\d\d|T\d|G3\d|G5[0-7])|Adreno \(TM\) (2\d\d|3\d\d|4\d\d|50\d|51\d|60\d|61[0-3]|70[0-5])|PowerVR (SGX|Rogue G[EX]?\d|GE\d|B-Series)|\bIMG\b|\bBX[ME]\b|VideoCore|Vivante|Tegra 3|SwiftShader|llvmpipe|Software|Microsoft Basic Render|Intel.*(HD Graphics [2-5]\d{3}|GMA|Bay ?Trail)/i.test(name);
  };
  OakRoom.prototype.setQuality = function (index, remember) {
    if (!this.ladder || !this.renderer) return;
    index = clamp(index, this.noEnv ? this.firstLite : 0, this.ladder.length - 1);
    var q = this.ladder[index];
    this.qIndex = index; this.quality = q;
    // the rebuilt buffer is blank: draw into it right away, or a black frame is shown
    if (this.applyBuffer() && this.ready && !this.contextLost) this.drawNow();
    if (q.lite !== !!this.liteActive) { if (this.ready) this.applyShadingAsync(q.lite); else this.applyShading(q.lite); }
    // Shadows are switched per mesh (receiveShadow is a plain shader uniform): nothing is recompiled. Turning the sun's
    // castShadow off instead would change the light setup and rebuild every shader, a freeze of seconds on a cheap phone.
    if (this.sun && this.sun.castShadow && q.shadows !== this.shadowsLive) this.setShadowsLive(q.shadows);
    if (remember) this.rememberQuality();
    this.dispatchEvent(new CustomEvent('oak:quality', { bubbles: true, detail: { index: index, ratio: q.ratio, lite: q.lite, shadows: q.shadows } }));
    this.requestFrame();
  };
  OakRoom.prototype.rememberQuality = function () {
    // a cheap phone loses a third of its GPU speed after a few minutes of heat: only what happened in the first minute
    // says something about the device itself, later steps are for this visit only
    if (this.readyAt && performance.now() - this.readyAt > 60000) return;
    try { window.localStorage.setItem('oak3d:quality', JSON.stringify({ i: this.qIndex, n: this.ladder.length, t: Date.now() })); } catch (e) { /* private mode */ }
  };
  OakRoom.prototype.setShadowsLive = function (on) {
    this.shadowsLive = on;
    this.allMeshes.forEach(function (m) { m.receiveShadow = on; });
    if (this.person) this.person.meshes.forEach(function (m) { m.receiveShadow = on; });
    if (on) this.renderer.shadowMap.needsUpdate = true;
  };
  /*
   * "Lite" shading: matte surfaces (nearly everything in this room) swap their physically based material for a Lambert
   * one that uses the SAME colour texture, so nothing gets blurrier. Small shiny things (brass, steel, mirror, glass)
   * keep their reflections. The Lambert shader also filters the sun's shadow with one tap instead of five (OAK_PCF1,
   * patched into three.js by build/vendor-entry.js): at this room's soft shadow contrast the difference is invisible.
   */
  OakRoom.prototype.liteOf = function (full) {
    var c = this.liteCache[full.uuid];
    if (c) return c;
    if (this.isHero(full)) c = full;
    else {
      c = new this.T.MeshLambertMaterial({
        name: full.name, color: full.color, map: full.map, emissive: full.emissive, emissiveIntensity: full.emissiveIntensity, emissiveMap: full.emissiveMap,
        side: full.side, alphaTest: full.alphaTest, opacity: full.opacity, depthWrite: full.depthWrite, vertexColors: full.vertexColors
      });
      c.defines = { OAK_PCF1: '' };
    }
    this.liteCache[full.uuid] = c;
    return c;
  };
  /* the shared material a mesh uses in the shading that is active right now */
  OakRoom.prototype.sharedFor = function (m) {
    var full = m.userData.matFull;
    return this.liteActive ? this.liteOf(full) : full;
  };
  OakRoom.prototype.applyShading = function (lite) {
    var el = this;
    this.liteActive = !!lite;
    this.allMeshes.forEach(function (m) { if (m.userData.matFull) m.material = el.sharedFor(m); });
    if (this.person) this.person.meshes.forEach(function (m) { m.material = el.sharedFor(m); });
    // with the environment map still on, Lambert surfaces only miss the faint mirror-like part of its light
    if (this.hemi && !this.noEnv) this.hemi.intensity = lite ? HEMI_LITE : HEMI_FULL;
    this.items.forEach(function (it) { if (it.claimed) { it.claimed = false; el.claimGlow(it); } });
  };
  /* Changing the shading while the room is running: the new shaders are compiled first, in the background where the
     driver allows it, on invisible stand-ins; only then are the materials swapped. No frozen frame on a slow phone. */
  OakRoom.prototype.applyShadingAsync = function (lite) {
    var el = this, T = this.T, standIns = new T.Group();
    this.allMeshes.concat(this.person ? this.person.meshes : []).forEach(function (m) {
      var full = m.userData.matFull;
      if (!full) return;
      var target = lite ? el.liteOf(full) : full;
      if (lite && target === full) return;   // shiny pieces keep the material they already have
      // the avatar's meshes are skinned, which is a different shader: their stand-in has to be skinned too
      // (not m.clone(): Object3D.copy() JSON-serialises userData.matFull, which PNG-encodes every texture on the main thread)
      var p = m.isSkinnedMesh ? new m.constructor(m.geometry, target) : new T.Mesh(m.geometry, target);
      p.material = target;
      p.receiveShadow = m.receiveShadow; p.matrixAutoUpdate = false;
      standIns.add(p);
    });
    this.pendingLite = !!lite;
    this.perfHold = Infinity;   // nothing is measured until the new materials are really on screen
    var finished = false;
    var done = function () {
      if (finished) return;
      finished = true;
      if (el.disposed || el.pendingLite !== !!lite || !el.renderer) return;
      el.applyShading(lite);
      el.perfHold = performance.now() + 1500;
      el.requestFrame();
    };
    try { this.renderer.compileAsync(standIns, this.camera, this.scene).then(done, done); } catch (e) { done(); }
    setTimeout(done, 6000);
  };
  /*
   * Glow. Materials are shared by many pieces, so a piece that lights up (hover, click) borrows its OWN copies for as
   * long as it glows and hands them back when it is at rest. At rest the whole room runs on ~25 materials instead of
   * ~150, which is what the CPU of a cheap phone pays for on every frame. Copies use the same shader: nothing compiles.
   */
  OakRoom.prototype.claimGlow = function (it) {
    var el = this, mats = [], seen = {};
    it.glowMeshes.forEach(function (m) {
      var shared = el.sharedFor(m);
      if (!shared) return;
      var c = it.cloneCache[shared.uuid];
      if (!c) {
        c = shared.clone();
        if (shared.defines) c.defines = Object.assign({}, shared.defines);   // clone() drops custom defines
        // remember the material's own emissive so the glow adds to it instead of replacing it (lamps stay lit)
        c.userData.baseEmissive = c.emissive ? c.emissive.clone() : null;
        it.cloneCache[shared.uuid] = c;
      }
      m.material = c;
      if (!seen[c.uuid]) { seen[c.uuid] = true; mats.push(c); }
    });
    it.mats = mats;
    it.claimed = true;
    // a piece can be at rest while it still glows (mouse resting on it): fresh copies must show the glow it has right now
    var g = it.glow || 0;
    for (var i = 0; i < mats.length; i++) {
      var cm = mats[i], be = cm.userData.baseEmissive;
      if (cm.emissive) cm.emissive.setRGB((be ? be.r : 0) + 0.86 * g, (be ? be.g : 0) + 0.6 * g, (be ? be.b : 0) + 0.28 * g);
    }
  };
  /* takes a piece (and what rides on it) out of the baked sun shadows, or puts it back: one shadow pass each time */
  OakRoom.prototype.setItemShadow = function (it, on) {
    it.glowMeshes.forEach(function (m) {
      if (m.userData.oakCast === undefined) m.userData.oakCast = m.castShadow;   // glass, lamps, curtains never cast
      m.castShadow = on ? m.userData.oakCast : false;
    });
    it.shadowOff = !on;
    this.renderer.shadowMap.needsUpdate = true;
  };
  OakRoom.prototype.releaseGlow = function (it) {
    var el = this;
    it.glowMeshes.forEach(function (m) { if (m.userData.matFull) m.material = el.sharedFor(m); });
    it.mats = [];
    it.claimed = false;
    it.glow = 0;
  };

  /* The step's pixel ratio, limited by a pixel budget: a big canvas (fullscreen walk mode on a tablet, a 4K monitor)
     never costs more than the screen the step was meant for. Never below one canvas pixel per CSS pixel. */
  OakRoom.prototype.ratioFor = function (w, h, q) {
    var dpr = window.devicePixelRatio || 1;
    q = q || this.quality;
    // drawing-buffer pixels per frame: weak phones 0.8 million, other phones 2 million, Apple GPUs and desktops 4 million
    var budget = (!this.phoneClass || /Apple/i.test(this.gpuName || '')) ? 4.0e6 : (this.weak ? 0.8e6 : 2.0e6);
    var r = Math.min(q.ratio, dpr, Math.sqrt(budget / Math.max(1, w * h)));
    return Math.max(Math.min(dpr, 1), Math.floor(r * 8) / 8);   // in eighths, so the buffer is not rebuilt for nothing
  };
  /* one reallocation of the (multisampled) drawing buffer, and only when its size really changes */
  OakRoom.prototype.applyBuffer = function () {
    var w = this.width || 1, h = this.height || 1, r = this.ratioFor(w, h);
    this.pixelRatio = r;
    var bw = Math.floor(w * r), bh = Math.floor(h * r);
    if (bw === this.bufW && bh === this.bufH) return false;
    this.bufW = bw; this.bufH = bh;
    this.renderer.setDrawingBufferSize(w, h, r);
    return true;
  };

  /* The governor starts measuring once loading work is over, and first draws a short full-rate burst to measure on. */
  OakRoom.prototype.armGovernor = function () {
    this.perfStart = performance.now() + 1500;
    this.calibFrames = 150;
    this.badWindows = 0;
  };
  /* How often this screen can show a frame at all (16.7ms at 60Hz, 8.3ms at 120Hz, but 33ms when a battery saver
     caps the browser at 30fps). Measured on ticks where nothing was drawn, so the GPU cannot be what slows them. */
  OakRoom.prototype.noteCadence = function (dt) {
    var c = this.cadenceSamples || (this.cadenceSamples = []);
    if (dt > 3 && dt < 100) c.push(dt);
    if (c.length >= 30) {
      c.sort(function (a, b) { return a - b; });
      this.cadence = clamp(c[6], 4, 50);   // the 20th percentile: robust against the odd busy tick
      c.length = 0;
    }
  };
  OakRoom.prototype.probeCadence = function () {
    var el = this, last = 0, n = 0;
    var tick = function (t) {
      if (el.disposed || el.ready) return;   // once the room runs, frame() keeps the estimate fresh
      if (last) el.noteCadence(t - last);
      last = t;
      if (++n < 90) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  /*
   * Steps down the quality ladder when the frame rate cannot be held. "counts" = this frame and the previous one were
   * both drawn back to back; throttled idle frames, loading, scrolling, transitions and showcases say nothing about the GPU.
   *  - the target is relative to what the screen can do (see noteCadence), so a 30fps battery saver is not "slow";
   *  - a step that does not make things faster was the wrong medicine (the phone is limited by its CPU, or capped):
   *    after two such steps in a row everything they took away is given back and the governor retires.
   */
  OakRoom.prototype.trackPerformance = function (now, counts) {
    if (this.qualityLocked || !counts || !this.perfStart || now < this.perfStart || now < (this.perfHold || 0) || now - (this.lastScrollAt || 0) < 400 ||
        this.transition || this.isShowcasing || (this.bvhQueue && this.bvhQueue.length)) { this.lastFrame = 0; return; }
    if (this.lastFrame) {
      var ft = now - this.lastFrame;
      if (ft < 250) this.frameTimes.push(ft);
      if (this.frameTimes.length >= 60) {
        // a trimmed mean: the slowest tenth (a stray garbage collection, an image decode) does not count
        var s = this.frameTimes.slice().sort(function (a, b) { return a - b; });
        var keep = Math.floor(s.length * 0.9), sum = 0;
        for (var i = 0; i < keep; i++) sum += s[i];
        var avg = sum / keep;
        this.frameTimes.length = 0;
        if (this.lastStep) {
          // did the previous step buy anything?
          if (avg < this.lastStep.avg * 0.9) { this.futile = 0; this.rememberQuality(); }   // only a step that helped is remembered
          else { if (!this.futile) this.futileFrom = this.lastStep.from; this.futile = (this.futile || 0) + 1; }
          this.lastStep = null;
          if (this.futile >= 2) {
            this.qualityLocked = true;
            this.setQuality(this.futileFrom, false);
            return;
          }
        }
        var slow = avg > Math.max(20, (this.cadence || 16.7) * 1.25);
        // Slow because of the CPU (JavaScript + draw-call submission), not the GPU? Then fewer pixels or cheaper shaders
        // cannot help: taking them away would only make the room blurrier and just as slow.
        var cpu = this.cpuMean || 0;
        if (slow && cpu > 0.55 * Math.max(16.7, this.cadence || 16.7)) { this.qualityLocked = true; this.lockReason = 'cpu-bound'; return; }
        this.badWindows = slow ? (this.badWindows || 0) + 1 : 0;
        if (this.badWindows >= 2) {
          // the next step that really changes something on THIS canvas (the pixel budget can make a ratio step a no-op)
          var next = -1, cur = this.quality, curR = this.pixelRatio;
          for (var j = this.qIndex + 1; j < this.ladder.length; j++) {
            var cand = this.ladder[j];
            if (cand.lite !== cur.lite || cand.shadows !== cur.shadows || this.ratioFor(this.width, this.height, cand) !== curR) { next = j; break; }
          }
          this.badWindows = 0;
          if (next >= 0) {
            this.perfHold = now + 1500;   // let the change settle before measuring again
            this.lastStep = { from: this.qIndex, avg: avg };
            this.setQuality(next, false);
          }
        }
      }
    }
    this.lastFrame = now;
  };

  /* Add ?oak3d=debug to the page address to see what the room decided on this device (handy on a real phone). */
  OakRoom.prototype.showDebug = function () {
    var pre = document.createElement('pre');
    pre.style.cssText = 'position:absolute;left:6px;bottom:6px;z-index:20;margin:0;padding:6px 8px;font:10px/1.35 monospace;color:#fff;background:rgba(0,0,0,.72);border-radius:6px;pointer-events:none;white-space:pre-wrap;max-width:70%';
    this.stage.appendChild(pre);
    this.debugEl = pre;
  };
  OakRoom.prototype.updateDebug = function (now) {
    this.dbgFrames = (this.dbgFrames || 0) + (this.drewLastTick ? 1 : 0);
    if (now - (this.dbgAt || 0) < 1000) return;
    var q = this.quality, info = this.renderer.info.render;
    this.debugEl.textContent =
      'step ' + this.qIndex + '/' + (this.ladder.length - 1) + (q.lite ? ' lite' : ' full') + (this.noEnv ? ' no-env' : '') + (this.shadowsLive ? ' shadows' : ' no-shadows') + (this.qualityLocked ? ' LOCKED' + (this.lockReason ? ':' + this.lockReason : '') : '') + '\n' +
      'ratio ' + this.pixelRatio + ' (dpr ' + (window.devicePixelRatio || 1).toFixed(2) + ') buffer ' + this.bufW + 'x' + this.bufH + '\n' +
      'drawn ' + this.dbgFrames + '/s  screen ' + (this.cadence ? (1000 / this.cadence).toFixed(0) + 'Hz' : '?') + '  cpu ' + (this.cpuMean || 0).toFixed(1) + 'ms\n' +
      'calls ' + info.calls + ' tris ' + info.triangles + ' programs ' + this.renderer.info.programs.length + '\n' +
      (this.phoneClass ? 'phone' : 'desktop') + (this.isTouch ? ' touch' : ' mouse') + (this.weak ? ' WEAK' : '') + '\n' + (this.gpuName || '');
    this.dbgFrames = 0; this.dbgAt = now;
  };

  /* A phone under memory pressure can take the WebGL context away. three.js re-creates its GPU objects by itself once the
     context returns; the environment map and the baked shadows are render targets and have to be drawn again. */
  OakRoom.prototype.buildEnvironment = function () {
    var T = this.T, old = this.envTex, tex;
    var pmrem = new T.PMREMGenerator(this.renderer);
    // nearly every surface here is matte, so phones get a quarter-size environment map (1.6MB instead of 6MB, 4x faster to make)
    tex = this.envTex = pmrem.fromScene(new T.RoomEnvironment(), 0.04, 0.1, 100, this.phoneClass ? { size: 128 } : undefined).texture;
    pmrem.dispose();
    // weak GPUs light the room without it (see initScene); shiny pieces carry it explicitly (see tuneMaterial)
    this.scene.environment = this.noEnv ? null : tex;
    (this.heroMats || []).forEach(function (m) { m.envMap = tex; });
    if (old) old.dispose();
  };
  OakRoom.prototype.bindContextLoss = function () {
    var el = this;
    this.onContextLost = function (e) { e.preventDefault(); el.contextLost = true; el.classList.add('is-paused'); };
    this.onContextRestored = function () {
      if (el.disposed || !el.renderer) return;
      try { el.buildEnvironment(); } catch (err) { /* ignore */ }
      // glow copies still point at the old environment map: drop them, they are made again on demand
      el.items.forEach(function (it) {
        if (it.claimed) el.releaseGlow(it);
        Object.keys(it.cloneCache).forEach(function (k) { it.cloneCache[k].dispose(); });
        it.cloneCache = {};
      });
      el.renderer.shadowMap.needsUpdate = true;
      el.frameTimes.length = 0; el.badWindows = 0; el.lastStep = null;
      el.perfHold = performance.now() + 3000;   // the shaders are compiled again: never let the governor count that
      var back = function () { el.contextLost = false; el.classList.remove('is-paused'); el.requestFrame(); };
      try { el.renderer.compileAsync(el.scene, el.camera).then(back, back); } catch (err) { back(); }
    };
    this.canvas.addEventListener('webglcontextlost', this.onContextLost);
    this.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
  };

  OakRoom.prototype.dispose = function () {
    this.disposed = true;
    if (this.bootIo) this.bootIo.disconnect();
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.documentElement.classList.remove('oak-walk-lock');
    clearTimeout(this.cardResetTimer);
    clearTimeout(this.hintTimer);
    if (this.stage && this.onWalkTouchMove) this.stage.removeEventListener('touchmove', this.onWalkTouchMove);
    if (this.canvas && this.onContextLost) {
      this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
      this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    }
    if (this.bvhHandle) { (window.cancelIdleCallback || clearTimeout)(this.bvhHandle); this.bvhHandle = null; }
    this.bvhQueue = [];
    if (this.ro) this.ro.disconnect();
    if (this.visIo) this.visIo.disconnect();
    this.ready = false;
    // free GPU memory: the theme editor re-creates this element on every settings change
    if (this.model) {
      this.model.traverse(function (n) {
        if (!n.isMesh) return;
        if (n.geometry) { if (n.geometry.disposeBoundsTree) n.geometry.disposeBoundsTree(); n.geometry.dispose(); }
        var m = n.material, full = n.userData.matFull;
        if (m) { if (m.map) m.map.dispose(); m.dispose(); }
        if (full && full !== m) full.dispose();   // the other shading's material (textures are shared, already freed)
      });
      this.model = null;
    }
    if (this.person) {
      this.person.meshes.forEach(function (m) {
        if (m.geometry) m.geometry.dispose();
        var pm = m.userData.matFull;
        if (pm) { if (pm.map) pm.map.dispose(); pm.dispose(); }
        if (m.skeleton) m.skeleton.dispose();
      });
      if (this.person.blob) { this.person.blob.material.map.dispose(); this.person.blob.material.dispose(); this.person.blob.geometry.dispose(); }
      this.person = null;
    }
    var lite = this.liteCache || {};
    Object.keys(lite).forEach(function (k) { lite[k].dispose(); });
    (this.items || []).forEach(function (it) { Object.keys(it.cloneCache || {}).forEach(function (k) { it.cloneCache[k].dispose(); }); });
    this.liteCache = {}; this.heroMats = [];
    if (this.showRing) {
      if (this.showRing.material.map) this.showRing.material.map.dispose();
      this.showRing.material.dispose(); this.showRing.geometry.dispose();
      this.showRing = null; this.ringItem = null;
    }
    if (this.envTex) { this.envTex.dispose(); this.envTex = null; }
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.forceContextLoss) this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.items = []; this.allMeshes = []; this.collidables = []; this.boomBlockers = []; this.markerEls = [];
  };

  OakRoom.PersonRig = PersonRig;   // for build/person-lab.html, where the walk cycle is tuned
  customElements.define('oak-room-3d', OakRoom);
})();
